// Liste Denetimi — secilen tasarimlari yayina kadar olan her adim icin
import { useShopVertical } from "../lib/useShopVertical";
import { WarpedAreaImage } from "../components/mockup/WarpedAreaImage";
import { hasWarp, isShaped, type AreaShape, type AreaFit, type AreaWarp } from "../lib/area-shape";
// gozden gecirip taslaga cevirir.
//
// Neden ayri bir ekran: kontroller bugune kadar farkli yerlere dagilmisti
// (kategori sablonda, kombinasyon tavani yayin aninda, gorsel siniri
// Studio'da) ve eksikler ancak Etsy reddedince goruluyordu. Burada hepsi
// tek listede, engel/uyari ayrimiyla gorunur; hazir olanlar tek tikla
// kuyruga alinir (mockup uretimi sunucuda, sayfa kapansa da surer).

import { useChatGptFilter, chatgptParam } from "../lib/useChatGptFilter";
import { ChatGptFilterButton } from "../components/ChatGptFilterButton";
import { useEffect, useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import {
  AlertTriangle, CheckCircle2, ChevronDown, ChevronLeft, ChevronRight, ClipboardCheck, ExternalLink,
  Bot, Image as ImageIcon, Loader2, Maximize2, Pencil, Plus, RefreshCw, Star, X, XCircle,
} from "lucide-react";
import { authHeaders, getBackendUrl } from "../lib/http";
import { apiClient } from "../lib/api";
import { ImageLightbox } from "../components/ImageLightbox";
import { MockupChooserModal } from "../components/mockup/MockupChooserModal";
import { AssetLibrary, assetApi } from "../components/listing/AssetLibrary";
import { DesignDetailModal } from "./DesignsPage";
import DesignEditorPanel from "../components/design/DesignEditorPanel";
import { SEO_FIX_MODELS, DEFAULT_SEO_FIX_MODEL, type Design } from "@erc/shared";

interface Issue { code: string; message: string }

interface ReviewMockup {
  id: string; label: string; theme: string;
  thumbnailUrl: string; imageUrl: string; included: boolean;
  canvasWidth: number; canvasHeight: number; opacity: number;
  /** Ön plan katmani: mockupta gizlenen alanlar tasarimin ustunde */
  foregroundUrl?: string | null;
  areas: Array<{ x: number; y: number; w: number; h: number; rotation: number; theme?: string; shape?: AreaShape; fit?: AreaFit; warp?: AreaWarp | null }>;
  /** Custom liste: her alanin tasarim havuzu indeksi */
  areaDesigns?: number[] | null;
}

type PoolItem = { id: string; label: string; lightUrl: string | null; darkUrl: string | null };

interface DesignReview {
  designId: string;
  /** Ilham listesinin ve sablonun tam Etsy kategorisi */
  inspirationCategory?: string | null;
  templateCategory?: string | null;
  name: string;
  sku: string | null;
  imageUrl: string;
  thumbnailUrl: string | null;
  lightUrl: string | null;
  darkUrl: string | null;
  hasDark: boolean;
  analysisId: string | null;
  analysisTitle: string | null;
  analysisImageUrl: string | null;
  copyright: { level: string; summary: string } | null;
  listedCount: number;
  plan: {
    productTypeKey: string | null;
    listingTemplateId: string | null;
    listingTemplateName: string | null;
    collectionIds: string[];
    collectionNames: string[];
    sellingMockupId: string | null;
    sellingMockupLabel: string | null;
  };
  counts: { mockups: number; templateImages: number; totalImages: number; variationAxes: number; combos: number };
  mockups: ReviewMockup[];
  /** Custom kolaj (listede ANA gorsel) ve kutuphaneden atanan ek gorseller */
  collageUrl?: string | null;
  collageUrls?: string[];
  extraImages?: string[];
  /** Coklu tasarimli (custom) liste: tasarim havuzu ve dagilimi */
  designPool?: PoolItem[];
  designMode?: "single" | "cycle" | "random" | "per_mockup";
  designSeed?: number;
  price: number | null;
  /** Big print liste: mockuplar 16 inc kopyalar, fiyat +$5 (sunucu ekledi) */
  bigPrint?: boolean;
  listing: {
    title: string; description: string; tags: string[]; quantity: number;
    taxonomyId: number | null; shippingProfileId: string | null;
    variations: unknown; variationName: string | null; personalization: unknown;
  } | null;
  blockers: Issue[];
  warnings: Issue[];
  /** chatgpt: ChatGPT'nin MCP ile gonderdigi hazir paket (SEO + kisisellestirme) */
  source?: string;
  fromChatGPT?: boolean;
  mainPhotoUrl?: string | null;
  mainPhotoSource?: string | null;
  canvaUrl?: string | null;
  sellingMockupSuggestion?: string | null;
  inspirationSuggestion?: string | null;
  isPackage?: boolean;
  packageInfo?: {
    hasTemplate: boolean; templateVersion: number | null;
    sampleRenders: Array<{ url: string; backUrl?: string | null; darkUrl?: string | null; values: Record<string, string> }>;
    sampleMode: "same" | "distributed";
    issues: Array<{ level: string; code: string; message: string }>;
    printSide: string | null; custom: boolean;
  } | null;
}

interface BuildJob {
  id: string; designId: string; status: string; draftId: string | null;
  imageCount: number; error: string | null; createdAt: string;
}

type Override = {
  collectionIds?: string[];
  listingTemplateId?: string;
  excludedMockupIds?: string[];
  /** Elle duzenlenen mockup listesi — sira ve icerik */
  mockupIds?: string[];
  /** Coklu tasarimli liste: dagilim modu, karistirma tohumu, mockup basina baslangic */
  designMode?: "single" | "cycle" | "random" | "per_mockup";
  designSeed?: number;
  designAssign?: Record<string, number>;
};

/** Mockup onizlemesi: tasarim baski alanina CSS ile bindirilir.
 *
 *  Studio'daki izgarada da ayni yaklasim var — sunucuda kompozit uretmek
 *  saniyeler suruyor, secim ekraninda beklemeye degmez. */
function MockupPreview({ m, lightUrl, darkUrl, size, pool }: {
  m: ReviewMockup;
  lightUrl: string | null;
  darkUrl: string | null;
  size: number;
  /** Coklu tasarim havuzu: m.areaDesigns ile alan basina tasarim */
  pool?: PoolItem[];
}) {
  const scale = size / Math.max(1, m.canvasWidth);
  const h = m.canvasHeight * scale;
  return (
    <div className="relative overflow-hidden rounded bg-gray-100" style={{ width: size, height: h }}>
      {/* abs(): sunucu yollari gorece geliyor, on ek olmadan yuklenmiyordu */}
      {/* Kucuk kartta kucuk gorsel; buyuk gosterimde (tam ekran) ASIL gorsel —
          kucuk gorsel buyutulunce bulanik gorunuyordu */}
      <img src={abs(size > 240 ? m.imageUrl : m.thumbnailUrl)} alt={m.label} loading="lazy" className="absolute inset-0 h-full w-full object-cover" />
      {m.areas.map((a, i) => {
        // abs() bos varyanti "" yapiyor; ?? bos metni atlamadigi icin koyu
        // mockupa tasarim hic basilmiyordu. || ile tek tasarim her yere basilir.
        const pd = pool && m.areaDesigns ? pool[m.areaDesigns[i] ?? 0] : null;
        const L = pd ? abs(pd.lightUrl) : lightUrl;
        const D = pd ? abs(pd.darkUrl) : darkUrl;
        const url = (a.theme ?? m.theme) === "dark" ? (D || L) : (L || D);
        if (!url || a.w <= 0 || a.h <= 0) return null;
        if (hasWarp(a)) return <WarpedAreaImage key={i} area={a} src={url} imageW={m.canvasWidth} imageH={m.canvasHeight} opacity={(m.opacity ?? 100) / 100} />;
        return (
          <img
            key={i}
            src={url}
            alt=""
            style={{
              position: "absolute",
              left: a.x * scale,
              top: a.y * scale,
              width: a.w * scale,
              height: a.h * scale,
              objectFit: "contain",
              // Dik dikdortgen alanda ust hizali (sunucudaki baski ile ayni); daire/kalp ortada
              objectPosition: !isShaped(a) && a.h >= a.w * 0.95 ? "center top" : "center",
              opacity: (m.opacity ?? 100) / 100,
              transform: a.rotation ? `rotate(${a.rotation}deg)` : undefined,
              transformOrigin: "top left",
            }}
          />
        );
      })}
      {/* Ön plan: el, sac, kol gibi gizlenen alanlar tasarimin USTUNE (Studio ile ayni) */}
      {m.foregroundUrl && (
        <img src={abs(m.foregroundUrl)} alt="" loading="lazy" className="pointer-events-none absolute inset-0 h-full w-full object-cover" />
      )}
    </div>
  );
}

const abs = (u?: string | null) => (!u ? "" : u.startsWith("http") ? u : `${getBackendUrl()}${u}`);
const LAST_TPL_KEY = "erc:review:last-template";
/** Liste Denetimi oturumu (secim + degisiklikler) */
const SESSION_KEY = "erc:review:session";
const FILTERS_KEY = "erc:review:picker-filters";
const SEO_MODEL_KEY = "erc:review:seo-model";

const GRID_COLS: Record<"s" | "m" | "l", string> = {
  s: "grid-cols-4 sm:grid-cols-6 lg:grid-cols-8",
  m: "grid-cols-3 sm:grid-cols-4 lg:grid-cols-6",
  l: "grid-cols-2 sm:grid-cols-3 lg:grid-cols-4",
};

/** Mockuplari TAM EKRAN gosterir — tasarim yerlestirilmis haliyle.
 *
 *  Duz lightbox yalnizca ham mockup fotografini aciyordu; denetimde asil
 *  bakilan sey tasarimin urun uzerindeki hali. Oklarla gezinir, Esc kapatir. */
function MockupFullscreen({ mockups, index, lightUrl, darkUrl, onIndex, onClose, pool }: {
  pool?: PoolItem[];
  mockups: ReviewMockup[];
  index: number;
  lightUrl: string | null;
  darkUrl: string | null;
  onIndex: (i: number) => void;
  onClose: () => void;
}) {
  const [size, setSize] = useState(() => Math.min(window.innerWidth - 120, window.innerHeight - 160));

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowRight") onIndex((index + 1) % mockups.length);
      else if (e.key === "ArrowLeft") onIndex((index - 1 + mockups.length) % mockups.length);
    };
    const onResize = () => setSize(Math.min(window.innerWidth - 120, window.innerHeight - 160));
    window.addEventListener("keydown", onKey);
    window.addEventListener("resize", onResize);
    return () => { window.removeEventListener("keydown", onKey); window.removeEventListener("resize", onResize); };
  }, [index, mockups.length, onIndex, onClose]);

  const m = mockups[index];
  if (!m) return null;

  return (
    <div className="fixed inset-0 z-[80] flex flex-col items-center justify-center bg-black/85 p-4" onClick={onClose}>
      <button onClick={onClose} className="absolute right-4 top-4 rounded-full bg-white/15 p-2 text-white hover:bg-white/25">
        <X className="h-5 w-5" />
      </button>

      <div onClick={(e) => e.stopPropagation()}>
        <MockupPreview m={m} lightUrl={lightUrl} darkUrl={darkUrl} size={size} pool={pool} />
      </div>

      {mockups.length > 1 && (
        <>
          <button
            onClick={(e) => { e.stopPropagation(); onIndex((index - 1 + mockups.length) % mockups.length); }}
            className="absolute left-4 top-1/2 -translate-y-1/2 rounded-full bg-white/15 p-2.5 text-white hover:bg-white/25"
          >
            <ChevronLeft className="h-6 w-6" />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onIndex((index + 1) % mockups.length); }}
            className="absolute right-4 top-1/2 -translate-y-1/2 rounded-full bg-white/15 p-2.5 text-white hover:bg-white/25"
          >
            <ChevronRight className="h-6 w-6" />
          </button>
        </>
      )}

      <p className="mt-3 text-xs text-white/80" onClick={(e) => e.stopPropagation()}>
        {m.label} · {index + 1}/{mockups.length}
      </p>
    </div>
  );
}

export default function ListingReviewPage() {
  // Big print ve koyu kumas (tema) tisorte ozgu: hediyelik/takida gizlenir
  const isApparel = useShopVertical().vertical === "apparel";
  // Metni urune uyarla (tisort ilhami -> sweatshirt/hoodie sablonu)
  const [adapting, setAdapting] = useState<string | null>(null);
  const [adaptErr, setAdaptErr] = useState<Record<string, string>>({});
  // Metin duzeltme modeli (ucuz GPT/Gemini); son secim hatirlanir
  const [seoModel, setSeoModel] = useState<string>(() => {
    try { const v = localStorage.getItem(SEO_MODEL_KEY); return SEO_FIX_MODELS.some((m) => m.id === v) ? v! : DEFAULT_SEO_FIX_MODEL; }
    catch { return DEFAULT_SEO_FIX_MODEL; }
  });
  const pickSeoModel = (v: string) => { setSeoModel(v); try { localStorage.setItem(SEO_MODEL_KEY, v); } catch { /* yok */ } };
  const adaptSeo = async (designId: string, listingTemplateId: string | null) => {
    if (!listingTemplateId) return;
    setAdapting(designId);
    setAdaptErr((e) => ({ ...e, [designId]: "" }));
    try {
      const r = await fetch(`${getBackendUrl()}/api/listing-review/adapt-seo`, {
        method: "POST", headers: authHeaders(), body: JSON.stringify({ designId, listingTemplateId, model: seoModel }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || "Metin uyarlanamadı");
      void refetchReview();
    } catch (e) {
      setAdaptErr((x) => ({ ...x, [designId]: e instanceof Error ? e.message : String(e) }));
    } finally {
      setAdapting(null);
    }
  };
  const qc = useQueryClient();
  // ?designIds=a,b ile gelinirse (analiz sayfasindan "Liste Denetimi'ne gonder") on secili
  // OTURUM TASLAGI: secim, sablon/grup/mockup degisiklikleri ve dagilim
  // tarayicida saklanir; sayfa yenilense ya da baska sekmeden donulse de kaybolmaz.
  const saved = (() => {
    try { return JSON.parse(localStorage.getItem(SESSION_KEY) || "null") as { selected?: string[]; overrides?: Record<string, Override>; withVideo?: boolean } | null; }
    catch { return null; }
  })();
  const [selected, setSelected] = useState<Set<string>>(() => {
    try {
      const ids = new URLSearchParams(window.location.search).get("designIds");
      // Baglantiyla gelen tasarimlar oturuma EKLENIR (mevcut secim korunur)
      return new Set([...(saved?.selected ?? []), ...(ids ? ids.split(",").filter(Boolean) : [])]);
    } catch { return new Set(); }
  });
  const [pickerOpen, setPickerOpen] = useState(false);
  // Liste paketleri bir dugmenin arkasinda (sayfa kalabaliklasmasin)
  const [packagesOpen, setPackagesOpen] = useState(false);
  const { data: pkgList = [] } = useQuery({ queryKey: ["packages"], queryFn: fetchPackages, refetchInterval: 30000 });
  const pkgWaiting = pkgList.filter((p) => p.status === "awaiting_mockup" && !selected.has(p.designId)).length;
  const [overrides, setOverrides] = useState<Record<string, Override>>(() => saved?.overrides ?? {});
  const [msg, setMsg] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [withVideo, setWithVideo] = useState(saved?.withVideo ?? true);
  useEffect(() => {
    try { localStorage.setItem(SESSION_KEY, JSON.stringify({ selected: [...selected], overrides, withVideo })); } catch { /* dolu */ }
  }, [selected, overrides, withVideo]);
  const [lightbox, setLightbox] = useState<
    { urls: string[]; index: number; captions?: Array<{ text?: string | null }> } | null
  >(null);

  // Secim modali filtreleri (Tasarimlar sayfasindakilerle ayni alanlar).
  // Son secimler hatirlanir: ayni suzgeci her acilista yeniden kurmak
  // gereksiz bir istekti.
  const savedFilters = (() => {
    try { return JSON.parse(localStorage.getItem(FILTERS_KEY) || "{}") as Record<string, unknown>; }
    catch { return {} as Record<string, unknown>; }
  })();
  const [search, setSearch] = useState(String(savedFilters.search ?? ""));
  const [source, setSource] = useState(String(savedFilters.source ?? ""));
  const [sort, setSort] = useState(String(savedFilters.sort ?? "newest"));
  const [copyright, setCopyright] = useState(String(savedFilters.copyright ?? ""));
  const [onlyNoListing, setOnlyNoListing] = useState(Boolean(savedFilters.onlyNoListing));
  const [onlyCustom, setOnlyCustom] = useState(Boolean(savedFilters.onlyCustom));
  const [onlyChatgpt, setOnlyChatgpt] = useChatGptFilter();
  /** Izgara boyu: kucuk/orta/buyuk — buyuk tasarimda ayrinti gorunsun */
  const [gridSize, setGridSize] = useState<"s" | "m" | "l">(
    (savedFilters.gridSize as "s" | "m" | "l") ?? "m",
  );
  // Modalde acik olan tasarim (duzenleme + secim ayni yerde)
  const [detailDesign, setDetailDesign] = useState<Design | null>(null);
  /** Modal denetim kartindan mi acildi (gezinme denetimdeki tasarimlar arasinda) */
  const [detailFromReview, setDetailFromReview] = useState<Design[] | null>(null);
  /** Gorsel editoru (sayfadan ayrilmadan) */
  const [editorDesign, setEditorDesign] = useState<Design | null>(null);
  const [editorMsg, setEditorMsg] = useState<string | null>(null);
  /** Surukle-birak sirasi */
  const [dragging, setDragging] = useState<{ designId: string; id: string } | null>(null);
  /** Hangi tasarim icin mockup ekleme modali acik */
  const [addFor, setAddFor] = useState<string | null>(null);
  /** Kutuphaneden ek gorsel eklenecek tasarim */
  const [assetFor, setAssetFor] = useState<string | null>(null);
  /** Tam ekran mockup goruntuleyici */
  const [mockupView, setMockupView] = useState<{ designId: string; index: number } | null>(null);
  const [showOriginal, setShowOriginal] = useState(false);

  useEffect(() => {
    try {
      localStorage.setItem(FILTERS_KEY, JSON.stringify({
        search, source, sort, copyright, onlyNoListing, onlyCustom, gridSize,
      }));
    } catch { /* ignore */ }
  }, [search, source, sort, copyright, onlyNoListing, onlyCustom, gridSize]);

  // Tasarim havuzu YALNIZCA secim modali acikken cekilir
  const { data: designs = [], isLoading: designsLoading } = useQuery({
    queryKey: ["designs", "review-picker", search, source, sort, copyright, onlyNoListing, onlyCustom, onlyChatgpt],
    enabled: pickerOpen,
    queryFn: async (): Promise<Design[]> => {
      const res = await apiClient.listDesigns({
        limit: 200,
        search: search.trim() || undefined,
        source: source || undefined,
        sort,
        copyright: copyright || undefined,
        noListing: onlyNoListing || undefined,
        custom: onlyCustom || undefined,
        chatgpt: chatgptParam(onlyChatgpt),
      });
      return Array.isArray(res) ? res : res.data;
    },
  });

  const { data: templates = [] } = useQuery({
    queryKey: ["listing-templates", "review"],
    queryFn: async () => {
      const r = await fetch(`${getBackendUrl()}/api/listing-templates`, { headers: authHeaders() });
      if (!r.ok) return [];
      return ((await r.json()).data ?? []) as Array<{ id: string; name: string; mockupCollectionIds?: string[] }>;
    },
  });

  const { data: collections = [] } = useQuery({
    queryKey: ["mockup-collections", "review"],
    queryFn: async () => {
      const r = await fetch(`${getBackendUrl()}/api/mockups/collections`, { headers: authHeaders() });
      if (!r.ok) return [];
      return ((await r.json()).data ?? []) as Array<{ id: string; name: string; mockups?: unknown[] }>;
    },
  });

  const ids = useMemo(() => [...selected], [selected]);

  const { data: review = [], isFetching: reviewing, refetch: refetchReview } = useQuery({
    queryKey: ["listing-review", ids.join(","), JSON.stringify(overrides)],
    enabled: ids.length > 0,
    // Sablon secince / mockup siralayinca kartlar bosalip geri geliyordu:
    // anahtar degisince React Query yeni sorguyu "bos" sayiyor. Onceki veri
    // ekranda kalsin, yalnizca sessizce tazelensin.
    placeholderData: keepPreviousData,
    queryFn: async (): Promise<DesignReview[]> => {
      const r = await fetch(`${getBackendUrl()}/api/listing-review`, {
        method: "POST",
        headers: { ...authHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({ designIds: ids, overrides }),
      });
      if (!r.ok) throw new Error("Denetim raporu alınamadı");
      return (await r.json()).data as DesignReview[];
    },
  });
  // Karttaki tasarima tiklayinca duzenleme modali; ileri/geri denetimdeki
  // tasarimlar arasinda gezer (liste arka planda yuklenir, modal hemen acilir)
  const openReviewDetail = async (designId: string) => {
    try {
      const d = await apiClient.getDesign(designId);
      setDetailDesign(d);
      setDetailFromReview([d]);
      const all = await Promise.all(review.map((x) => (x.designId === designId ? Promise.resolve(d) : apiClient.getDesign(x.designId).catch(() => null))));
      setDetailFromReview(all.filter((x): x is Design => Boolean(x)));
    } catch (e) {
      setEditorMsg(`Tasarım açılamadı: ${e instanceof Error ? e.message : String(e)}`);
    }
  };
  // Gorsel editoru kaydi: duzenlenmis PNG yuklenir, modal ve denetim tazelenir
  const saveEditor = async (dataUrl: string) => {
    if (!editorDesign) return;
    try {
      const blob = await (await fetch(dataUrl)).blob();
      await apiClient.uploadEditedDesign(editorDesign.id, new File([blob], `edited-${Date.now()}.png`, { type: "image/png" }));
      const updated = await apiClient.getDesign(editorDesign.id);
      setEditorDesign(null);
      if (detailDesign?.id === updated.id) setDetailDesign(updated);
      setDetailFromReview((l) => l?.map((x) => (x.id === updated.id ? updated : x)) ?? null);
      void qc.invalidateQueries({ queryKey: ["designs", "review-picker"] });
      void refetchReview();
      setEditorMsg("Düzenleme kaydedildi");
    } catch (e) {
      setEditorMsg(`Düzenleme kaydedilemedi: ${e instanceof Error ? e.message : String(e)}`);
    }
  };
  useEffect(() => { if (!editorMsg) return undefined; const t = setTimeout(() => setEditorMsg(null), 4000); return () => clearTimeout(t); }, [editorMsg]);
  // Duzenleme yeni sekmede yapiliyor: bu sekmeye donulunce denetim tazelenir
  useEffect(() => {
    const onFocus = () => { if (document.visibilityState === "visible") void refetchReview(); };
    document.addEventListener("visibilitychange", onFocus);
    return () => document.removeEventListener("visibilitychange", onFocus);
  }, [refetchReview]);

  const { data: jobs = [], refetch: refetchJobs } = useQuery({
    queryKey: ["build-queue"],
    queryFn: async (): Promise<BuildJob[]> => {
      const r = await fetch(`${getBackendUrl()}/api/listing-drafts/build-queue`, { headers: authHeaders() });
      if (!r.ok) return [];
      return ((await r.json()).data ?? []) as BuildJob[];
    },
    refetchInterval: (q) => {
      const rows = (q.state.data ?? []) as BuildJob[];
      return rows.some((j) => j.status === "pending" || j.status === "processing") ? 4000 : false;
    },
  });

  const enqueue = useMutation({
    mutationFn: async (items: DesignReview[]) => {
      // Her tasarimin KENDI grubu/sablonu/eledigi mockuplari olabilir
      let queued = 0;
      const doneIds: string[] = [];
      const blocked: string[] = [];
      for (const it of items) {
        // Marka (Comfort Colors) / urun (sweatshirt) uyarisi varsa once metin duzeltilir;
        // basarisiz olursa taslak yine olusur (marka kurali taslakta kodla da uygulanir)
        if (it.plan.listingTemplateId && it.warnings.some((w) => w.code === "brand_mismatch" || w.code === "product_mismatch")) {
          setAdapting(it.designId);
          await fetch(`${getBackendUrl()}/api/listing-review/adapt-seo`, {
            method: "POST", headers: authHeaders(), body: JSON.stringify({ designId: it.designId, listingTemplateId: it.plan.listingTemplateId, model: seoModel }),
          }).catch(() => null);
          setAdapting(null);
        }
        const r = await fetch(`${getBackendUrl()}/api/listing-drafts/build-queue`, {
          method: "POST",
          headers: { ...authHeaders(), "Content-Type": "application/json" },
          body: JSON.stringify({
            designIds: [it.designId],
            // Ekranda gorunen SIRA ve icerik aynen uretilsin: ilk sira ana gorsel.
            // Grup yerine acik liste gonderilir ki sira korunsun.
            mockupIds: it.mockups.filter((m) => m.included).map((m) => m.id),
            collectionIds: [],
            listingTemplateId: it.plan.listingTemplateId ?? undefined,
            withVideo,
            // Coklu tasarim: onizlemedeki dagilim aynen uretilsin
            designOptions: (it.designPool?.length ?? 0) > 1
              ? { [it.designId]: { mode: it.designMode ?? "cycle", seed: it.designSeed ?? 1, assign: overrides[it.designId]?.designAssign ?? {} } }
              : undefined,
          }),
        });
        if (r.ok) {
          const data = (await r.json()).data ?? {};
          const n = (data.queued ?? 0) as number;
          queued += n;
          if (n > 0) doneIds.push(it.designId);
          for (const b of (data.blocked ?? []) as Array<{ reason: string }>) blocked.push(b.reason);
        }
      }
      return { queued, doneIds, blocked };
    },
    onSuccess: ({ queued, doneIds, blocked }) => {
      // Kuyruga alinanlar denetimden cikar — liste temiz kalsin
      if (doneIds.length) {
        setSelected((prev) => { const n = new Set(prev); for (const id of doneIds) n.delete(id); return n; });
        setChecked((prev) => { const n = new Set(prev); for (const id of doneIds) n.delete(id); return n; });
        setOverrides((prev) => { const n = { ...prev }; for (const id of doneIds) delete n[id]; return n; });
      }
      setMsg(`${queued} tasarım kuyruğa alındı${withVideo ? " (video dahil)" : ""} ve denetimden çıkarıldı — sayfayı kapatabilirsin.`
        + (blocked.length ? ` Taslağa çevrilmedi (üye tasarım sorunları): ${blocked.join(" | ")}` : ""));
      void refetchJobs();
    },
  });

  // Kart secimi (toplu islemler icin) — denetim listesindeki "secili" tasarimlardan ayri
  const [checked, setChecked] = useState<Set<string>>(new Set());
  const toggleChecked = (id: string) => setChecked((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const reviewIds = review.map((r) => r.designId);
  const allChecked = reviewIds.length > 0 && reviewIds.every((id) => checked.has(id));
  const alreadyListed = review.filter((r) => r.warnings.some((w) => w.code === "already_listed")).map((r) => r.designId);
  /** Tasarimlari denetimden cikar (secim + degisiklikler) */
  function removeFromReview(idsToRemove: string[]) {
    if (!idsToRemove.length) return;
    setSelected((prev) => { const n = new Set(prev); for (const id of idsToRemove) n.delete(id); return n; });
    setChecked((prev) => { const n = new Set(prev); for (const id of idsToRemove) n.delete(id); return n; });
    setOverrides((prev) => { const n = { ...prev }; for (const id of idsToRemove) delete n[id]; return n; });
  }
  function clearAll() {
    if (!confirm(`Denetim listesindeki ${selected.size} tasarım temizlenecek (tasarımlar silinmez, yalnızca bu listeden çıkar). Devam edilsin mi?`)) return;
    setSelected(new Set()); setChecked(new Set()); setOverrides({});
    setMsg(null);
  }

  const ready = review.filter((r) => r.blockers.length === 0);
  // Kart secimi varsa yalnizca secilenler taslaga cevrilir
  const readyToQueue = checked.size > 0 ? ready.filter((r) => checked.has(r.designId)) : ready;
  const blocked = review.filter((r) => r.blockers.length > 0);
  const overLimit = review.filter((r) => r.counts.totalImages > 20);

  /** Sablona hic grup bagli degilse, denetimde secilen grubu SABLONA bagla ve
   *  ayni sablonu kullanan (grubu olmayan) diger tasarimlara da uygula.
   *  Boylece bir kez secmek yeter; sonraki listelerde grup kendiliginden gelir. */
  async function bindGroupIfTemplateHasNone(templateId: string | null, collectionId: string) {
    if (!templateId) return;
    const tpl = templates.find((t) => t.id === templateId);
    if (!tpl || (tpl.mockupCollectionIds?.length ?? 0) > 0) return;
    const r = await fetch(`${getBackendUrl()}/api/listing-templates/${templateId}`, {
      method: "PATCH",
      headers: { ...authHeaders(), "Content-Type": "application/json" },
      body: JSON.stringify({ mockupCollectionIds: [collectionId] }),
    }).catch(() => null);
    if (!r?.ok) return;
    setOverrides((prev) => {
      const next = { ...prev };
      for (const row of review) {
        if (row.plan.listingTemplateId === templateId && row.plan.collectionIds.length === 0) {
          next[row.designId] = { ...next[row.designId], collectionIds: [collectionId], excludedMockupIds: [] };
        }
      }
      return next;
    });
    const name = collections.find((c) => c.id === collectionId)?.name ?? "grup";
    setMsg(`"${name}" grubu "${tpl.name}" şablonuna bağlandı — bu şablonu kullanan listelerde artık kendiliğinden gelir.`);
    void qc.invalidateQueries({ queryKey: ["listing-templates"] });
  }

  const toggle = (id: string) =>
    setSelected((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const patch = (designId: string, p: Override) =>
    setOverrides((prev) => ({ ...prev, [designId]: { ...prev[designId], ...p } }));

  /** BIG PRINT ac/kapa: satirdaki mockuplar big print kopyalariyla (ya da
   *  kaynaklariyla) degisir; sunucu listede big print gorunce fiyata +$5 ekler */
  async function toggleBigPrint(r: DesignReview) {
    try {
      const all = (await apiClient.listMockups("mockup")) as Array<{ id: string; bigPrint?: boolean; bigPrintSourceId?: string | null; createdAt?: string }>;
      const ids = r.mockups.filter((m) => m.included).map((m) => m.id);
      if (r.bigPrint) {
        // Kapat: kopyalar kaynaklarina doner
        const src = new Map(all.filter((m) => m.bigPrint && m.bigPrintSourceId).map((m) => [m.id, m.bigPrintSourceId as string]));
        const back = [...new Set(ids.map((id) => src.get(id) ?? id))].filter((id) => !all.find((m) => m.id === id)?.bigPrint);
        patch(r.designId, { mockupIds: back, excludedMockupIds: [] });
        return;
      }
      const copyOf = new Map<string, string>();
      for (const m of [...all].sort((a, b) => String(b.createdAt).localeCompare(String(a.createdAt)))) {
        if (m.bigPrint && m.bigPrintSourceId && !copyOf.has(m.bigPrintSourceId)) copyOf.set(m.bigPrintSourceId, m.id);
      }
      const next = [...new Set(ids.map((id) => copyOf.get(id)).filter((x): x is string => Boolean(x)))];
      if (next.length === 0) {
        setMsg("Bu listedeki mockupların big print kopyası yok. Mockup Yönetimi'nde mockup kartından 'Big print kopyası oluştur' ile hazırla.");
        return;
      }
      patch(r.designId, { mockupIds: next, excludedMockupIds: [] });
      if (next.length < ids.length) setMsg(`${ids.length - next.length} mockupun big print kopyası yok; listeden çıkarıldı.`);
    } catch (err) {
      setMsg("Big print uygulanamadı: " + (err instanceof Error ? err.message : String(err)));
    }
  }

  /** Secili TUM tasarimlara sablon uygula (son secim hatirlanir) */
  function applyTemplateToAll(templateId: string) {
    if (!templateId) return;
    try { localStorage.setItem(LAST_TPL_KEY, templateId); } catch { /* ignore */ }
    const tpl = templates.find((t) => t.id === templateId);
    setOverrides((prev) => {
      const next = { ...prev };
      for (const id of ids) {
        next[id] = {
          ...next[id],
          listingTemplateId: templateId,
          // Sablona bagli gruplar varsa onlar gelsin
          collectionIds: tpl?.mockupCollectionIds?.length ? tpl.mockupCollectionIds : next[id]?.collectionIds,
          excludedMockupIds: [],
          mockupIds: undefined,
        };
      }
      return next;
    });
  }

  function applyCollectionToAll(collectionId: string) {
    if (!collectionId) return;
    setOverrides((prev) => {
      const next = { ...prev };
      for (const id of ids) next[id] = { ...next[id], collectionIds: [collectionId], excludedMockupIds: [], mockupIds: undefined };
      return next;
    });
  }

  useEffect(() => { if (ids.length === 0) setMsg(null); }, [ids.length]);

  const lastTpl = (() => { try { return localStorage.getItem(LAST_TPL_KEY) ?? ""; } catch { return ""; } })();

  // Ust cubuk son secilen sablonu GOSTERIYOR ama uygulamiyordu: sablonu
  // olmayan tasarimlara son sablonu (ve ona bagli mockup grubunu) gercekten uygula.
  useEffect(() => {
    if (!lastTpl || templates.length === 0) return;
    const tpl = templates.find((t) => t.id === lastTpl);
    if (!tpl) return;
    const missing = review.filter((r) => !r.plan.listingTemplateId && !overrides[r.designId]?.listingTemplateId);
    if (missing.length === 0) return;
    setOverrides((prev) => {
      const next = { ...prev };
      for (const r of missing) {
        next[r.designId] = {
          ...next[r.designId],
          listingTemplateId: tpl.id,
          collectionIds: tpl.mockupCollectionIds?.length ? tpl.mockupCollectionIds : next[r.designId]?.collectionIds,
          excludedMockupIds: [],
        };
      }
      return next;
    });
  }, [review, templates, lastTpl]); // eslint-disable-line react-hooks/exhaustive-deps

  // Toplu secim kutusu: hepsinde AYNI sablon varsa onu goster, yoksa bos
  const commonTpl = review.length > 0 && review.every((r) => r.plan.listingTemplateId === review[0].plan.listingTemplateId)
    ? review[0].plan.listingTemplateId ?? ""
    : "";

  return (
    <div className="mx-auto max-w-[1600px] p-6">
      <div className="mb-4 flex items-start justify-between gap-4">
        <div>
          <h1 className="flex items-center gap-2 text-xl font-bold text-gray-900">
            <ClipboardCheck className="h-5 w-5 text-indigo-600" /> Liste Denetimi
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            Seçilen tasarımların eksiklerini gör, düzelt, hazır olanları taslağa çevir.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {pkgList.length > 0 && (
            <button
              onClick={() => setPackagesOpen((o) => !o)}
              className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium ${packagesOpen ? "border-violet-400 bg-violet-50 text-violet-800" : "border-gray-200 text-gray-700 hover:bg-gray-50"}`}
            >
              <Bot className="h-4 w-4 text-violet-600" /> Liste paketleri
              {pkgWaiting > 0 && <span className="rounded-full bg-violet-600 px-1.5 text-[11px] font-semibold text-white">{pkgWaiting}</span>}
            </button>
          )}
          <button
            onClick={() => setPickerOpen(true)}
            className="flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700"
          >
            <ImageIcon className="h-4 w-4" /> Tasarımları Seç{selected.size > 0 ? ` (${selected.size})` : ""}
          </button>
          <button
            onClick={() => void refetchReview()}
            disabled={ids.length === 0 || reviewing}
            className="flex items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-50"
          >
            {reviewing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Yenile
          </button>
          <label className="flex items-center gap-1.5 rounded-lg border border-gray-200 px-2.5 py-2 text-xs text-gray-600"
            title="Taslağa çevirirken marka (Comfort Colors) ve ürün (sweatshirt) uyarısı olan metinler önce otomatik düzeltilir; başlığı bu model yeniden kurar">
            Metin modeli
            <select value={seoModel} onChange={(e) => pickSeoModel(e.target.value)} aria-label="Metin düzeltme modeli"
              className="bg-transparent text-xs font-medium text-gray-800 focus:outline-none">
              {SEO_FIX_MODELS.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
            </select>
          </label>
          <label className="flex items-center gap-1.5 rounded-lg border border-gray-200 px-2.5 py-2 text-xs text-gray-600"
            title="Slayt video sunucuda üretilir ve taslağa eklenir">
            <input type="checkbox" checked={withVideo} onChange={(e) => setWithVideo(e.target.checked)} />
            Video
          </label>
          <button
            onClick={() => enqueue.mutate(readyToQueue)}
            disabled={readyToQueue.length === 0 || enqueue.isPending}
            className="flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
          >
            {enqueue.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
            {withVideo ? "Video + Taslağa çevir" : "Taslağa çevir"} ({readyToQueue.length}{checked.size > 0 ? " seçili" : ""})
          </button>
        </div>
      </div>

      {/* ChatGPT'nin gonderdigi, mockup bekleyen paketler */}
      {packagesOpen && <ChatGptPackages selected={selected} onAdd={(add) => setSelected((cur) => new Set([...cur, ...add]))} />}

      {/* Secim ve temizlik */}
      {ids.length > 0 && (
        <div className="mb-3 flex flex-wrap items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs">
          <label className="flex cursor-pointer items-center gap-1.5 font-medium text-gray-700">
            <input type="checkbox" checked={allChecked}
              onChange={() => setChecked(allChecked ? new Set() : new Set(reviewIds))} />
            {allChecked ? "Seçimi kaldır" : "Tümünü seç"}
          </label>
          {checked.size > 0 && (
            <>
              <span className="text-gray-500">{checked.size} seçili</span>
              <button onClick={() => removeFromReview([...checked])}
                className="flex items-center gap-1 rounded border border-gray-200 px-2 py-1 text-gray-600 hover:border-red-300 hover:text-red-600">
                <X className="h-3 w-3" /> Seçilenleri çıkar
              </button>
              <button onClick={() => setChecked(new Set(reviewIds.filter((id) => ready.some((r) => r.designId === id))))}
                className="rounded border border-gray-200 px-2 py-1 text-gray-600 hover:bg-gray-50">Yalnızca hazır olanlar</button>
            </>
          )}
          {checked.size === 0 && ready.length > 0 && ready.length < reviewIds.length && (
            <button onClick={() => setChecked(new Set(ready.map((r) => r.designId)))}
              className="rounded border border-gray-200 px-2 py-1 text-gray-600 hover:bg-gray-50">Hazır olanları seç ({ready.length})</button>
          )}
          {alreadyListed.length > 0 && (
            <button onClick={() => removeFromReview(alreadyListed)}
              title="Bu tasarımlardan daha önce liste açılmış"
              className="rounded border border-amber-200 bg-amber-50 px-2 py-1 text-amber-800 hover:bg-amber-100">
              Listesi açılanları çıkar ({alreadyListed.length})
            </button>
          )}
          <button onClick={clearAll}
            className="ml-auto flex items-center gap-1 rounded border border-red-200 px-2 py-1 text-red-600 hover:bg-red-50">
            <X className="h-3 w-3" /> Temizle
          </button>
        </div>
      )}

      {/* Toplu uygulama */}
      {ids.length > 0 && (
        <div className="mb-3 flex flex-wrap items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2">
          <span className="text-xs font-medium text-gray-600">Seçili {ids.length} tasarıma uygula:</span>
          <select
            value={commonTpl}
            onChange={(e) => applyTemplateToAll(e.target.value)}
            className="rounded border border-gray-200 px-2 py-1 text-xs"
          >
            <option value="">Listing şablonu…</option>
            {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
          <select
            defaultValue=""
            onChange={(e) => applyCollectionToAll(e.target.value)}
            className="rounded border border-gray-200 px-2 py-1 text-xs"
          >
            <option value="">Mockup grubu…</option>
            {collections.map((c) => (
              <option key={c.id} value={c.id}>{c.name} ({c.mockups?.length ?? 0})</option>
            ))}
          </select>
          <span className="text-[11px] text-gray-400">
            Şablona grup bağlıysa grup kendiliğinden gelir (Şablonlar sayfasından bağlanır).
          </span>
        </div>
      )}

      {msg && (
        <div className="mb-3 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">{msg}</div>
      )}
      {/* Gorsel siniri: mockup + sablon gorselleri (beden/renk tablosu, talimatlar) 20'yi asan listeler hemen gorunsun */}
      {overLimit.length > 0 && (
        <div className="mb-3 rounded-lg border-2 border-red-300 bg-red-50 px-3 py-2 text-sm text-red-800">
          <p className="font-semibold">{overLimit.length} listede görsel sayısı 20'yi aşıyor — Etsy en fazla 20 görsel kabul eder.</p>
          <p className="mt-0.5 text-xs">Mockup + şablon görselleri (beden tablosu, renk tablosu, talimatlar…) birlikte sayılır. Mockup çıkar ya da başka şablon seç.</p>
          <div className="mt-1.5 flex flex-wrap gap-1.5">
            {overLimit.map((r) => (
              <button key={r.designId} onClick={() => document.getElementById(`rv-${r.designId}`)?.scrollIntoView({ behavior: "smooth", block: "center" })}
                className="rounded-full border border-red-300 bg-white px-2 py-0.5 text-xs font-medium text-red-700 hover:bg-red-100">
                {r.sku || r.name} · {r.counts.totalImages}/20
              </button>
            ))}
          </div>
        </div>
      )}
      {blocked.length > 0 && (
        <div className="mb-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
          {blocked.length} tasarım engelli — düzeltilene kadar kuyruğa alınmaz.
        </div>
      )}

      <div className="space-y-3">
        {ids.length === 0 ? (
          <div className="rounded-xl border-2 border-dashed border-gray-200 bg-gray-50/50 py-20 text-center">
            <p className="text-sm font-medium text-gray-600">Önce tasarım seç</p>
            <p className="mt-1 text-xs text-gray-400">Seçilen her tasarım için eksikler burada listelenir.</p>
            <button
              onClick={() => setPickerOpen(true)}
              className="mx-auto mt-3 flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700"
            >
              <ImageIcon className="h-4 w-4" /> Tasarımları Seç
            </button>
          </div>
        ) : review.map((r) => {
          const isOpen = expanded.has(r.designId);
          return (
            <div key={r.designId} id={`rv-${r.designId}`}
              className={`scroll-mt-24 rounded-xl border bg-white p-3 ${r.counts.totalImages > 20 ? "border-2 border-red-500 ring-2 ring-red-100" : r.blockers.length > 0 ? "border-red-200" : "border-emerald-200"}`}>
              {r.counts.totalImages > 20 && (
                <div className="-mx-3 -mt-3 mb-3 rounded-t-xl bg-red-600 px-3 py-1.5 text-xs font-semibold text-white">
                  Görsel {r.counts.totalImages}/20 — {r.counts.totalImages - 20} fazla ({r.counts.mockups} mockup + {r.counts.templateImages} şablon görseli). Mockup çıkar ya da başka şablon seç.
                </div>
              )}
              <div className="flex gap-3">
                {/* Tasarim + ilham listesi fotografi */}
                <div className="flex w-28 shrink-0 flex-col gap-1">
                  <div className="group/zoom relative overflow-hidden rounded-lg bg-alpha-check">
                    <button onClick={() => void openReviewDetail(r.designId)} title="Tasarımı düzenle" className="block">
                      <img src={abs(r.thumbnailUrl || r.imageUrl)} alt="" className="h-28 w-28 object-contain transition-opacity hover:opacity-90" />
                    </button>
                    <button
                      onClick={() => setLightbox({ urls: [abs(r.imageUrl)], index: 0, captions: [{ text: r.name }] })}
                      className="absolute right-1 top-1 hidden rounded bg-white/90 p-0.5 text-gray-600 shadow-sm hover:text-gray-900 group-hover/zoom:block"
                      title="Tam ekran"
                    >
                      <Maximize2 className="h-3 w-3" />
                    </button>
                  </div>
                  {r.analysisImageUrl && (
                    <div className="group/ilham relative">
                      <img src={r.analysisImageUrl} alt="" loading="lazy"
                        className="h-20 w-28 rounded-lg border border-amber-200 object-cover" />
                      <span className="absolute left-1 top-1 rounded bg-black/55 px-1 text-[8px] text-white">ilham</span>
                      <div className="pointer-events-none absolute left-full top-0 z-30 ml-2 hidden w-72 overflow-hidden rounded-lg border border-gray-200 bg-white p-1 shadow-2xl group-hover/ilham:block">
                        <img src={r.analysisImageUrl} alt="" className="h-auto w-full rounded object-contain" />
                        {r.analysisTitle && (
                          <p className="mt-1 line-clamp-2 px-0.5 text-[10px] text-gray-600">{r.analysisTitle}</p>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                <div className="min-w-0 flex-1">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex min-w-0 items-start gap-2">
                      <input type="checkbox" checked={checked.has(r.designId)} onChange={() => toggleChecked(r.designId)}
                        className="mt-1 h-4 w-4 shrink-0" title="Toplu işlem için seç" />
                      <div className="min-w-0">
                      <p className="truncate text-sm font-semibold text-gray-900">{r.name}</p>
                      <p className="text-[11px] text-gray-500">
                        {r.sku && r.sku !== r.name ? `${r.sku} · ` : ""}
                        {r.plan.productTypeKey ?? "ürün tipi yok"}
                        {r.price != null && ` · $${r.price}`}
                        {r.bigPrint && (
                          <span className="ml-1.5 rounded bg-orange-500 px-1.5 py-0.5 text-[10px] font-semibold text-white"
                            title="Big print mockup seçildi: diğer mockuplar big print kopyalarıyla değişti, tüm fiyatlara +$5 eklendi">
                            Big print · +$5
                          </span>
                        )}
                      </p>
                      </div>
                    </div>
                    <div className="flex shrink-0 items-center gap-1.5">
                    {isApparel && <button
                      onClick={() => void toggleBigPrint(r)}
                      title={r.bigPrint
                        ? "Big print açık: mockuplar 16 inç kopyalar, fiyat +$5. Kapatmak için tıkla"
                        : "Big print (16 inç DTF): mockuplar big print kopyalarıyla değişir, tüm fiyatlara +$5 eklenir"}
                      className={`flex items-center gap-1 rounded px-2 py-0.5 text-[11px] font-medium ${r.bigPrint ? "bg-orange-500 text-white hover:bg-orange-600" : "border border-orange-200 text-orange-700 hover:bg-orange-50"}`}
                    >
                      {r.bigPrint ? "Big print ✓" : "Big print"}
                    </button>}
                    <button
                      onClick={() => window.open(`/designs?edit=${r.designId}`, "_blank")}
                      title="Tasarımı düzenle (açık/koyu varyant, Canva güncelleme...) — değişiklik listeye yansır"
                      className="flex items-center gap-1 rounded border border-gray-200 px-2 py-0.5 text-[11px] text-gray-600 hover:border-indigo-300 hover:text-indigo-600"
                    >
                      <Pencil className="h-3 w-3" /> Düzenle
                    </button>
                    <button
                      onClick={() => toggle(r.designId)}
                      title="Bu tasarımı denetim listesinden çıkar"
                      className="flex items-center gap-1 rounded border border-gray-200 px-2 py-0.5 text-[11px] text-gray-600 hover:border-red-300 hover:text-red-600"
                    >
                      <X className="h-3 w-3" /> Listeden çıkar
                    </button>
                    <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-medium ${
                      r.blockers.length > 0 ? "bg-red-50 text-red-700"
                        : r.warnings.length > 0 ? "bg-amber-50 text-amber-700"
                        : "bg-emerald-50 text-emerald-700"
                    }`}>
                      {r.blockers.length > 0 ? "Engel" : r.warnings.length > 0 ? "Uyarı" : "Hazır"}
                    </span>
                    </div>
                  </div>

                  {/* Secimler */}
                  <div className="mt-2 grid grid-cols-2 gap-2">
                    <label className="block">
                      <span className="mb-0.5 block text-[10px] uppercase tracking-wider text-gray-400">Listing şablonu</span>
                      <select
                        value={r.plan.listingTemplateId ?? ""}
                        onChange={(e) => {
                          const tpl = templates.find((t) => t.id === e.target.value);
                          patch(r.designId, {
                            listingTemplateId: e.target.value,
                            collectionIds: tpl?.mockupCollectionIds?.length ? tpl.mockupCollectionIds : undefined,
                            excludedMockupIds: [],
                            // Elle siralanmis/eklenmis liste sablon degisince birakilir: yeni grup gelsin
                            mockupIds: undefined,
                          });
                          try { if (e.target.value) localStorage.setItem(LAST_TPL_KEY, e.target.value); } catch { /* ignore */ }
                        }}
                        className={`w-full rounded border px-2 py-1 text-xs ${r.plan.listingTemplateId ? "border-gray-200" : "border-red-300 bg-red-50"}`}
                      >
                        <option value="">Şablon seç…</option>
                        {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                      </select>
                    </label>
                    <label className="block">
                      <span className="mb-0.5 block text-[10px] uppercase tracking-wider text-gray-400">Mockup grubu</span>
                      <select
                        value={r.plan.collectionIds[0] ?? ""}
                        onChange={(e) => {
                          const cid = e.target.value;
                          patch(r.designId, { collectionIds: cid ? [cid] : [], excludedMockupIds: [], mockupIds: undefined });
                          if (cid) void bindGroupIfTemplateHasNone(r.plan.listingTemplateId, cid);
                        }}
                        className={`w-full rounded border px-2 py-1 text-xs ${r.plan.collectionIds.length ? "border-gray-200" : "border-red-300 bg-red-50"}`}
                      >
                        <option value="">Grup seç…</option>
                        {collections.map((c) => (
                          <option key={c.id} value={c.id}>{c.name} ({c.mockups?.length ?? 0})</option>
                        ))}
                      </select>
                    </label>
                  </div>

                  {/* Sayaclar */}
                  <div className="mt-2 flex flex-wrap gap-1.5 text-[10px]">
                    <span className={`rounded px-1.5 py-0.5 ${r.counts.totalImages > 20 ? "bg-red-50 text-red-700" : "bg-gray-100 text-gray-600"}`}>
                      Görsel {r.counts.totalImages}/20 ({r.counts.mockups} mockup + {r.counts.templateImages} şablon)
                    </span>
                    <span className={`rounded px-1.5 py-0.5 ${r.counts.combos > 400 ? "bg-red-50 text-red-700" : "bg-gray-100 text-gray-600"}`}>
                      {r.counts.variationAxes} eksen · {r.counts.combos} kombinasyon
                    </span>
                    <span className="rounded bg-gray-100 px-1.5 py-0.5 text-gray-600">
                      {r.plan.sellingMockupLabel ? `Satan: ${r.plan.sellingMockupLabel.slice(0, 22)}` : "Satan mockup yok"}
                    </span>
                    {r.hasDark && <span className="rounded bg-indigo-50 px-1.5 py-0.5 text-indigo-600">koyu varyant var</span>}
                    {r.fromChatGPT && (
                      <span className="rounded bg-violet-50 px-1.5 py-0.5 text-violet-700" title="Başlık, açıklama, etiket ve kişiselleştirme ChatGPT'den">
                        ChatGPT paketi
                      </span>
                    )}
                    {r.isPackage && <MainPhotoControl designId={r.designId} url={r.mainPhotoUrl ?? null} source={r.mainPhotoSource ?? null} onChange={() => void qc.invalidateQueries({ queryKey: ["listing-review"] })} />}
                    {r.canvaUrl && (
                      <a href={r.canvaUrl} target="_blank" rel="noreferrer" className="rounded bg-sky-50 px-1.5 py-0.5 text-sky-700 hover:underline" title="Tasarımın düzenlenebilir aslı">
                        Canva'da düzenle <ExternalLink className="inline h-2.5 w-2.5" />
                      </a>
                    )}
                    {r.copyright && r.copyright.level !== "low" && (
                      <span className="rounded bg-amber-50 px-1.5 py-0.5 text-amber-700">telif: {r.copyright.level}</span>
                    )}
                    {r.analysisId && (
                      <Link to={`/analyses/${r.analysisId}`} className="rounded bg-indigo-50 px-1.5 py-0.5 text-indigo-600 hover:underline">
                        İlham listesi <ExternalLink className="inline h-2.5 w-2.5" />
                      </Link>
                    )}
                    {r.inspirationCategory && (
                      <span className="rounded bg-gray-100 px-1.5 py-0.5 text-gray-600"
                        title={`İlham kategorisi: ${r.inspirationCategory}${r.templateCategory ? `
Şablon kategorisi: ${r.templateCategory}` : ""}`}>
                        ilham kategorisi: {r.inspirationCategory.split(" > ").pop()}
                      </span>
                    )}
                  </div>
                  <ChatGptSuggestions selling={r.sellingMockupSuggestion} inspiration={r.inspirationSuggestion} />
                  {r.isPackage && r.packageInfo && (
                    <PackageTemplatePanel designId={r.designId} info={r.packageInfo} onChange={() => void qc.invalidateQueries({ queryKey: ["listing-review"] })} />
                  )}

                  {/* Engeller ve uyarilar */}
                  {(r.blockers.length > 0 || r.warnings.length > 0) && (
                    <ul className="mt-2 space-y-0.5">
                      {r.blockers.map((b) => (
                        <li key={b.code} className="flex items-start gap-1 text-[11px] text-red-700">
                          <XCircle className="mt-0.5 h-3 w-3 shrink-0" /> {b.message}
                        </li>
                      ))}
                      {r.warnings.map((w) => (
                        <li key={w.code} className="flex items-start gap-1 text-[11px] text-amber-700">
                          <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" /> {w.message}
                          {(w.code === "product_mismatch" || w.code === "brand_mismatch") && (
                            <button
                              onClick={() => void adaptSeo(r.designId, r.plan.listingTemplateId)}
                              disabled={adapting === r.designId}
                              title="Başlık, açıklama ve etiketler şablonun ürününe ve markasına uyarlanır (başlıkta yalnız Comfort Colors); niş kelimeler korunur"
                              className="ml-1 shrink-0 rounded border border-amber-300 bg-white px-1.5 py-0.5 text-[10px] font-medium text-amber-800 hover:bg-amber-50 disabled:opacity-50"
                            >
                              {adapting === r.designId ? "Uyarlanıyor…" : w.code === "brand_mismatch" ? "Markayı düzelt" : "Metni uyarla"}
                            </button>
                          )}
                          {(w.code === "product_mismatch" || w.code === "brand_mismatch") && adaptErr[r.designId] && (
                            <span className="ml-1 text-red-600">{adaptErr[r.designId]}</span>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}

                  {/* Mockuplar: tasarim YERLESTIRILMIS haliyle, dogrudan gorunur.
                      Surukle-birak ile siralanir; ilk sira ANA GORSELDIR. */}
                  <div className="mt-3">
                    <div className="mb-1 flex items-center justify-between">
                      <p className="text-[11px] font-medium text-gray-600">
                        Mockuplar ({r.mockups.filter((m) => m.included).length}) — sürükleyerek sırala, ilk sıra ana görsel
                        {(r.designPool?.length ?? 0) > 1 && (
                          <span className="ml-2 inline-flex items-center gap-1 font-normal">
                            · {r.designPool!.length} tasarım:
                            <select value={r.designMode ?? "cycle"}
                              onChange={(e) => patch(r.designId, { designMode: e.target.value as "single" | "cycle" | "random" | "per_mockup", designAssign: {} })}
                              className="rounded border border-gray-200 px-1 py-0 text-[11px]">
                              {r.isPackage && <option value="per_mockup">Mockup başına bir isim (ön/arka aynı)</option>}
                              {!r.isPackage && <option value="cycle">Sırayla dağıt</option>}
                              <option value="random">Karışık dağıt</option>
                              <option value="single">Tek tasarım</option>
                            </select>
                            {r.designMode === "random" && (
                              <button onClick={() => patch(r.designId, { designSeed: (r.designSeed ?? 1) + 1, designAssign: {} })}
                                className="rounded border border-gray-200 px-1.5 text-[11px] text-gray-600 hover:bg-gray-50">Yeniden karıştır</button>
                            )}
                            {Object.keys(overrides[r.designId]?.designAssign ?? {}).length > 0 && (
                              <button onClick={() => patch(r.designId, { designAssign: {} })}
                                className="rounded border border-gray-200 px-1.5 text-[11px] text-gray-600 hover:bg-gray-50">Elle değişiklikleri sıfırla</button>
                            )}
                          </span>
                        )}
                      </p>
                      <button
                        onClick={() => setAddFor(r.designId)}
                        className="flex items-center gap-1 rounded border border-indigo-200 px-2 py-0.5 text-[11px] text-indigo-600 hover:bg-indigo-50"
                      >
                        <Plus className="h-3 w-3" /> Mockup Ekle
                      </button>
                    </div>
                    {/* Studio'daki urun gorselleri gibi: kare kutu, sol ustte sira (1 = yildiz),
                        sag ustte eylemler (hover), altta coklu tasarim oklari; tikla = tam ekran */}
                    <div className="grid grid-cols-4 gap-2.5 sm:grid-cols-6 xl:grid-cols-8">
                      {r.mockups.map((m, i) => (
                        <div
                          key={m.id}
                          draggable
                          onDragStart={() => setDragging({ designId: r.designId, id: m.id })}
                          onDragOver={(e) => e.preventDefault()}
                          onDrop={(e) => {
                            e.preventDefault();
                            if (!dragging || dragging.designId !== r.designId) return;
                            const ids = r.mockups.map((x) => x.id);
                            const from = ids.indexOf(dragging.id);
                            const to = ids.indexOf(m.id);
                            if (from < 0 || to < 0 || from === to) return;
                            ids.splice(to, 0, ids.splice(from, 1)[0]);
                            patch(r.designId, { mockupIds: ids });
                            setDragging(null);
                          }}
                          onClick={() => setMockupView({ designId: r.designId, index: i })}
                          title="Tam ekran göster (sürükleyerek sırala)"
                          className={`group/mk relative cursor-zoom-in overflow-hidden rounded-lg border-2 transition-all active:cursor-grabbing ${
                            i === 0 && m.included ? "border-amber-400 ring-2 ring-amber-100" : "border-gray-200 hover:border-gray-300"
                          } ${m.included ? "" : "opacity-40"}`}
                        >
                          <div className="flex aspect-square items-center justify-center overflow-hidden bg-gray-50">
                            <MockupPreview m={m} lightUrl={abs(r.lightUrl)} darkUrl={abs(r.darkUrl)} size={160}
                              pool={(r.designPool?.length ?? 0) > 1 ? r.designPool : undefined} />
                          </div>

                          {/* Sol ust: sira (1. = ana gorsel yildizi) */}
                          {i === 0 && m.included ? (
                            <div className="absolute left-1 top-1 flex items-center gap-0.5 rounded bg-amber-400 px-1 py-0.5 text-[8px] font-bold text-white">
                              <Star className="h-2 w-2" />1
                            </div>
                          ) : (
                            <div className="absolute left-1 top-1 rounded bg-black/60 px-1.5 py-0.5 text-[8px] font-bold text-white">{i + 1}</div>
                          )}
                          {isApparel && m.theme === "dark" && (
                            <span className="absolute left-1 top-6 rounded bg-gray-900/70 px-1 text-[8px] text-white">koyu</span>
                          )}

                          {/* Sag ust: eylemler (hover) */}
                          <div className="absolute right-1 top-1 flex gap-1 opacity-0 transition-opacity group-hover/mk:opacity-100">
                            {i !== 0 && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const ids = r.mockups.map((x) => x.id);
                                  ids.splice(0, 0, ids.splice(i, 1)[0]);
                                  patch(r.designId, { mockupIds: ids });
                                }}
                                title="Ana görsel yap"
                                className="rounded bg-amber-400/90 p-1 shadow hover:bg-amber-500"
                              >
                                <Star className="h-3.5 w-3.5 text-white" />
                              </button>
                            )}
                            <button
                              onClick={(e) => { e.stopPropagation(); patch(r.designId, { mockupIds: r.mockups.filter((x) => x.id !== m.id).map((x) => x.id) }); }}
                              title="Listeden çıkar"
                              className="rounded bg-red-500/90 p-1 shadow hover:bg-red-600"
                            >
                              <X className="h-3.5 w-3.5 text-white" />
                            </button>
                          </div>

                          {/* Alt: coklu tasarim oklari */}
                          {(r.designPool?.length ?? 0) > 1 && m.included && m.areaDesigns && (() => {
                            const pool = r.designPool!;
                            const start = m.areaDesigns[0] ?? 0;
                            const setStart = (v: number) => patch(r.designId, {
                              designAssign: { ...(overrides[r.designId]?.designAssign ?? {}), [m.id]: (v + pool.length) % pool.length },
                            });
                            return (
                              <div className="absolute inset-x-0 bottom-0 flex items-center justify-between gap-1 bg-black/55 px-1 py-0.5 text-[10px] text-white"
                                onClick={(e) => e.stopPropagation()}>
                                <button onClick={() => setStart(start - 1)} title="Önceki tasarım" className="rounded px-1 hover:bg-white/20">◀</button>
                                <span className="min-w-0 flex-1 truncate text-center" title={m.areaDesigns.map((ix) => pool[ix]?.label).join(" · ")}>
                                  {m.areaDesigns.length > 1 ? `${pool[start]?.label} +${m.areaDesigns.length - 1}` : pool[start]?.label}
                                </span>
                                <button onClick={() => setStart(start + 1)} title="Sonraki tasarım" className="rounded px-1 hover:bg-white/20">▶</button>
                              </div>
                            );
                          })()}
                        </div>
                      ))}
                      {r.mockups.length === 0 && (
                        <span className="text-[11px] text-gray-400">Grup seçilmedi — şablona bağlı grup yoksa yukarıdan seç.</span>
                      )}
                    </div>
                  </div>

                  {/* Kolaj + ek gorseller: kolaj ANA gorsel, ek gorseller satan mockuptan sonra */}
                  <div className="mt-3">
                    <div className="mb-1 flex items-center justify-between">
                      <p className="text-[11px] font-medium text-gray-600">
                        Ek görseller ({(r.extraImages ?? []).length}) — kartela, beden tablosu… (listede satan mockup'tan sonra)
                      </p>
                      <button onClick={() => setAssetFor(r.designId)}
                        className="flex items-center gap-1 rounded border border-amber-200 px-2 py-0.5 text-[11px] text-amber-700 hover:bg-amber-50">
                        <Plus className="h-3 w-3" /> Kütüphaneden ekle
                      </button>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {(r.collageUrls?.length ? r.collageUrls : r.collageUrl ? [r.collageUrl] : []).map((u, ci) => (
                        <div key={u} className="relative">
                          <img src={abs(u)} alt="" className="h-[110px] w-auto rounded border border-fuchsia-300 bg-white object-contain" />
                          <span className="absolute left-1 top-1 rounded bg-fuchsia-600 px-1 text-[9px] font-medium text-white">
                            {ci === 0 ? "kolaj · ana" : `kolaj ${ci + 1}`}
                          </span>
                        </div>
                      ))}
                      {(r.extraImages ?? []).map((u) => (
                        <div key={u} className="group/ex relative">
                          <img src={abs(u)} alt="" className="h-[110px] w-auto rounded border border-amber-200 bg-white object-contain" />
                          <button
                            onClick={() => void assetApi.unassign(r.designId, u).then(() => refetchReview())}
                            title="Bu tasarımdan kaldır"
                            className="absolute right-1 top-1 hidden rounded bg-black/55 p-0.5 text-white group-hover/ex:block hover:bg-red-600"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </div>
                      ))}
                      {!r.collageUrl && (r.extraImages ?? []).length === 0 && (
                        <span className="text-[11px] text-gray-400">Yok</span>
                      )}
                    </div>
                  </div>

                  {/* Liste yapisi (varsayilan KAPALI) */}
                  <button
                    onClick={() => setExpanded((p) => { const n = new Set(p); n.has(r.designId) ? n.delete(r.designId) : n.add(r.designId); return n; })}
                    className="mt-2 flex items-center gap-1 text-[11px] font-medium text-indigo-600 hover:underline"
                  >
                    {isOpen ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                    Oluşacak liste yapısı
                  </button>

                  {isOpen && r.listing && (
                    <div className="mt-2 rounded border border-gray-200 bg-white p-2 text-[11px]">
                      <SeoEditor designId={r.designId} listing={r.listing} onSaved={() => void refetchReview()} />
                      <p className="mt-0.5 text-gray-600">
                        <span className="text-gray-400">Kategori:</span> {r.listing.taxonomyId ?? "—"}
                        {" · "}<span className="text-gray-400">Kargo:</span> {r.listing.shippingProfileId ?? "—"}
                        {" · "}<span className="text-gray-400">Stok:</span> {r.listing.quantity}
                      </p>

                      {/* Varyasyon sablonu: yalnizca ADI (yoksa eksen adlari) */}
                      {Array.isArray(r.listing.variations) && r.listing.variations.length > 0 && (
                        <p className="mt-0.5 text-gray-600">
                          <span className="text-gray-400">Varyasyon:</span>{" "}
                          <span className="font-medium text-gray-800">
                            {r.listing.variationName
                              ?? (r.listing.variations as Array<{ name?: string }>).map((v) => v.name ?? "?").join(" × ")}
                          </span>
                          <span className="text-gray-400"> · {r.counts.combos} kombinasyon</span>
                        </p>
                      )}

                      {/* Kisisellestirme */}
                      {(() => {
                        const p = r.listing.personalization as { enabled?: boolean; questions?: Array<{ question_text?: string }> } | null;
                        if (!p?.enabled) return null;
                        return (
                          <div className="mt-1.5 border-t border-gray-100 pt-1.5">
                            <p className="text-gray-400">Kişiselleştirme ({p.questions?.length ?? 0})</p>
                            {(p.questions ?? []).map((q, qi) => (
                              <p key={qi} className="text-gray-700">· {q.question_text}</p>
                            ))}
                          </div>
                        );
                      })()}
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}

        {/* Kuyruk durumu */}
        {/* Kuyruk yalnizca is varken gorunur — bos alan yer kaplamasin */}
        {jobs.some((j) => j.status === "pending" || j.status === "processing"
          || Date.now() - new Date(j.createdAt).getTime() < 30 * 60_000) && (
          <div className="rounded-xl border border-gray-200 bg-white p-3">
            <p className="mb-2 text-xs font-semibold text-gray-700">Kuyruk</p>
            <div className="space-y-1">
              {jobs.slice(0, 12).map((j) => (
                <div key={j.id} className="flex items-center justify-between gap-2 text-[11px]">
                  <span className="min-w-0 flex-1 truncate text-gray-600">
                    {review.find((r) => r.designId === j.designId)?.name ?? j.designId}
                  </span>
                  <span className={`rounded px-1.5 py-0.5 ${
                    j.status === "done" ? "bg-emerald-50 text-emerald-700"
                      : j.status === "error" ? "bg-red-50 text-red-700"
                      : "bg-indigo-50 text-indigo-700"
                  }`}>
                    {j.status === "done" ? `${j.imageCount} görsel` : j.status === "error" ? (j.error ?? "hata").slice(0, 40) : j.status}
                  </span>
                  {j.draftId && (
                    <Link to={`/listing-builder?id=${j.draftId}`} className="text-indigo-600 hover:underline">Taslağı aç</Link>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {lightbox && (
        <ImageLightbox
          urls={lightbox.urls}
          index={lightbox.index}
          captions={lightbox.captions}
          onClose={() => setLightbox(null)}
          onIndex={(i) => setLightbox((lb) => (lb ? { ...lb, index: i } : lb))}
        />
      )}

      {pickerOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setPickerOpen(false)}>
          <div className="flex h-[90vh] w-full max-w-7xl flex-col overflow-hidden rounded-xl bg-white shadow-xl"
            onClick={(e) => e.stopPropagation()}>
            <div className="flex flex-wrap items-center gap-2 border-b p-4">
              <h3 className="flex items-center gap-2 text-base font-semibold text-gray-900">
                <ImageIcon className="h-4 w-4 text-indigo-600" /> Tasarım Seç
              </h3>
              <span className="text-xs text-gray-400">{selected.size} seçili · {designs.length} tasarım</span>

              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="İsim ya da SKU ara…"
                className="ml-auto w-52 rounded-lg border border-gray-200 px-3 py-1.5 text-sm"
              />
              <select value={source} onChange={(e) => setSource(e.target.value)}
                className="rounded-lg border border-gray-200 px-2 py-1.5 text-xs">
                <option value="">Tüm kaynaklar</option>
                <option value="upload">Yüklenen</option>
                <option value="mockup-studio">Mockup Studio</option>
                <option value="design-builder">Design Builder</option>
                <option value="canva">Canva</option>
                <option value="chatgpt">ChatGPT</option>
              </select>
              <select value={copyright} onChange={(e) => setCopyright(e.target.value)}
                className="rounded-lg border border-gray-200 px-2 py-1.5 text-xs">
                <option value="">Tüm telif</option>
                <option value="low">Telif: düşük</option>
                <option value="medium">Telif: orta</option>
                <option value="high">Telif: yüksek</option>
              </select>
              <select value={sort} onChange={(e) => setSort(e.target.value)}
                className="rounded-lg border border-gray-200 px-2 py-1.5 text-xs">
                <option value="newest">En yeni</option>
                <option value="oldest">En eski</option>
                <option value="name">İsim</option>
              </select>
              <label className="flex items-center gap-1 text-xs text-gray-600">
                <input type="checkbox" checked={onlyNoListing} onChange={(e) => setOnlyNoListing(e.target.checked)} />
                Listesi olmayanlar
              </label>
              <label className="flex items-center gap-1 text-xs text-gray-600">
                <input type="checkbox" checked={onlyCustom} onChange={(e) => setOnlyCustom(e.target.checked)} />
                Custom
              </label>
              <ChatGptFilterButton compact value={onlyChatgpt} onChange={setOnlyChatgpt} />
              {/* Gorunum boyutu — buyuk tasarimda ayrinti secilir */}
              <div className="flex rounded-lg border border-gray-200 text-[11px]">
                {(["s", "m", "l"] as const).map((g) => (
                  <button key={g} onClick={() => setGridSize(g)}
                    className={`px-2 py-1 ${gridSize === g ? "bg-indigo-600 text-white" : "text-gray-600 hover:bg-gray-50"} ${
                      g === "s" ? "rounded-l-lg" : g === "l" ? "rounded-r-lg" : ""
                    }`}>
                    {g === "s" ? "Küçük" : g === "m" ? "Orta" : "Büyük"}
                  </button>
                ))}
              </div>
              <button onClick={() => setPickerOpen(false)} className="rounded p-1 text-gray-400 hover:bg-gray-100">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4">
              {designsLoading ? (
                <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-gray-400" /></div>
              ) : designs.length === 0 ? (
                <p className="py-20 text-center text-sm text-gray-400">Filtreye uyan tasarım yok.</p>
              ) : (
                <div className={`grid gap-3 ${GRID_COLS[gridSize]}`}>
                  {designs.map((d: Design) => {
                    const on = selected.has(d.id);
                    const cr = d.copyright?.level ?? null;
                    const insp = d.sourceAnalysis?.imageUrl ?? null;
                    const full = abs(d.editedUrl || d.originalUrl);
                    return (
                      <div key={d.id}
                        className={`overflow-hidden rounded-lg border-2 transition-colors ${
                          on ? "border-indigo-500 ring-2 ring-indigo-200" : "border-gray-200 hover:border-indigo-300"
                        }`}>
                        <div className="relative">
                        <button onClick={() => toggle(d.id)} className="relative block w-full">
                          <div className="aspect-square bg-alpha-check">
                            <img src={abs(d.thumbnailUrl || d.editedUrl || d.originalUrl)} alt={d.name}
                              loading="lazy" className="h-full w-full object-contain" />
                          </div>
                          {on && (
                            <span className="absolute right-1 top-1 flex h-5 w-5 items-center justify-center rounded-full bg-indigo-600 text-white">
                              <CheckCircle2 className="h-3.5 w-3.5" />
                            </span>
                          )}
                          {(d.listings?.length ?? 0) > 0 && (
                            <span className="absolute left-1 top-1 rounded bg-black/55 px-1 text-[9px] text-white">
                              {d.listings!.length} liste
                            </span>
                          )}
                        </button>

                        {/* Ilham listesi: tasarimin SOL ALT kosesinde, uzerine
                            gelince buyur */}
                        {insp && (
                          <div className="group/ilham absolute bottom-1 left-1 z-10">
                            <img src={insp} alt="" loading="lazy"
                              className="h-11 w-11 rounded border-2 border-white object-cover shadow" />
                            <span className="absolute left-0.5 top-0.5 rounded bg-black/55 px-0.5 text-[7px] text-white">ilham</span>
                            <div className="pointer-events-none absolute bottom-full left-0 z-40 mb-1 hidden w-64 overflow-hidden rounded-lg border border-gray-200 bg-white p-1 shadow-2xl group-hover/ilham:block">
                              <img src={insp} alt="" className="h-auto w-full rounded object-contain" />
                              {d.sourceAnalysis?.title && (
                                <p className="mt-1 line-clamp-2 px-0.5 text-[10px] text-gray-600">{d.sourceAnalysis.title}</p>
                              )}
                            </div>
                          </div>
                        )}
                        </div>

                        <div className="p-1.5">
                          <p className="truncate text-[11px] font-medium text-gray-800">{d.name}</p>
                          <div className="mt-0.5 flex flex-wrap items-center gap-1">
                            {/* Telif her zaman gorunur — "dusuk" de bilgi */}
                            {cr && (
                              <span className={`rounded px-1 py-0.5 text-[9px] ${
                                cr === "high" ? "bg-red-50 text-red-600"
                                  : cr === "medium" ? "bg-amber-50 text-amber-700"
                                  : "bg-emerald-50 text-emerald-700"
                              }`}>telif: {cr === "low" ? "düşük" : cr === "medium" ? "orta" : "yüksek"}</span>
                            )}
                            {d.custom && <span className="rounded bg-pink-50 px-1 py-0.5 text-[9px] text-pink-600">custom</span>}
                            {d.sku && d.sku !== d.name && (
                              <span className="rounded bg-gray-100 px-1 py-0.5 text-[9px] text-gray-500">{d.sku}</span>
                            )}
                          </div>

                          <div className="mt-1 flex gap-1">
                            <button
                              onClick={() => setDetailDesign(d)}
                              className="flex flex-1 items-center justify-center gap-1 rounded border border-gray-200 py-1 text-[10px] text-gray-600 hover:bg-gray-50"
                            >
                              <Pencil className="h-3 w-3" /> Düzenle
                            </button>
                            <button
                              onClick={() => setLightbox({
                                urls: [full, ...(insp ? [insp] : [])],
                                index: 0,
                                captions: [{ text: d.name }, ...(insp ? [{ text: "İlham listesi" }] : [])],
                              })}
                              title="Tam ekran"
                              className="rounded border border-gray-200 px-1.5 py-1 text-gray-500 hover:bg-gray-50"
                            >
                              <Maximize2 className="h-3 w-3" />
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="flex items-center justify-between border-t p-3">
              <button onClick={() => setSelected(new Set())} className="text-xs text-gray-500 hover:text-gray-700">
                Seçimi temizle
              </button>
              <button
                onClick={() => setPickerOpen(false)}
                className="rounded-lg bg-indigo-600 px-5 py-2 text-sm font-medium text-white hover:bg-indigo-700"
              >
                Tamam ({selected.size})
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Tasarim duzenleme + secme TEK modalda (Tasarimlar sayfasiyla ayni) */}
      {detailDesign && (() => {
        const list = detailFromReview ?? (designs as Design[]);
        const i = list.findIndex((x) => x.id === detailDesign.id);
        const close = () => {
          setDetailDesign(null);
          if (detailFromReview) {
            setDetailFromReview(null);
            // Canva guncellemesi / varyant ekleme modal disi yollardan da
            // olabilir: kapaninca denetimi tazele ki liste yeni tasarimi kullansin
            void qc.invalidateQueries({ queryKey: ["designs", "review-picker"] });
            void refetchReview();
          }
        };
        const next = i >= 0 && i < list.length - 1 ? list[i + 1] : null;
        const prev = i > 0 ? list[i - 1] : null;
        return (
          <DesignDetailModal
            design={detailDesign}
            showOriginal={showOriginal}
            onToggleOriginal={() => setShowOriginal((v) => !v)}
            onClose={close}
            onDelete={close}
            onRevert={close}
            onOpenEditor={() => setEditorDesign(detailDesign)}
            onLinkAnalysis={() => window.open(`/designs`, "_blank")}
            onRemoveAnalysis={() => { /* denetimde analiz kaldirma yok */ }}
            onSellingMockupChange={() => { void refetchReview(); }}
            onUpdate={async (payload) => {
              await apiClient.updateDesign(detailDesign.id, payload);
              const updated = await apiClient.getDesign(detailDesign.id);
              setDetailDesign(updated);
              setDetailFromReview((l) => l?.map((x) => (x.id === updated.id ? updated : x)) ?? null);
              // Izgara ve denetim raporu eski gorseli gostermesin
              void qc.invalidateQueries({ queryKey: ["designs", "review-picker"] });
              void refetchReview();
            }}
            onRefresh={async () => {
              const updated = await apiClient.getDesign(detailDesign.id);
              setDetailDesign(updated);
              setDetailFromReview((l) => l?.map((x) => (x.id === updated.id ? updated : x)) ?? null);
              void qc.invalidateQueries({ queryKey: ["designs", "review-picker"] });
              void refetchReview();
            }}
            selectMode={{
              selected: selected.has(detailDesign.id),
              onToggle: () => toggle(detailDesign.id),
              hasNext: Boolean(next),
              hasPrev: Boolean(prev),
              onNext: () => { if (next) setDetailDesign(next); },
              onPrev: () => { if (prev) setDetailDesign(prev); },
              index: Math.max(0, i),
              total: list.length,
              onSelectAndNext: () => {
                setSelected((prev) => new Set(prev).add(detailDesign.id));
                if (next) setDetailDesign(next);
              },
            }}
          />
        );
      })()}

      {editorDesign && (
        // Tasarim modalinin (z-50) ustunde
        <div className="relative z-[65]">
        <DesignEditorPanel
          designUrl={abs(editorDesign.editedUrl || editorDesign.originalUrl)}
          onClose={() => setEditorDesign(null)}
          onSave={saveEditor}
        />
        </div>
      )}
      {editorMsg && (
        <div className="fixed bottom-4 left-1/2 z-[70] -translate-x-1/2 rounded-full bg-gray-900 px-4 py-2 text-sm text-white shadow-lg">{editorMsg}</div>
      )}

      {/* Mockup ekleme — satan mockup secimiyle AYNI modal: tum havuz, arama,
          klasor suzgeci, alan belirleme */}
      {addFor && (() => {
        const row = review.find((x) => x.designId === addFor);
        const ids = (row?.mockups ?? []).map((m) => m.id);
        const coll = collections.find((c) => c.id === row?.plan.collectionIds[0]);
        return (
          <MockupChooserModal
            title="Mockupları düzenle — seçilen ana görsel olur"
            mode="multi"
            newestFirst
            // Ustte listedeki mockuplar duzenlenir; havuzdan secilen BASA eklenir (ana gorsel)
            current={{ ids, onChange: (next) => patch(addFor, { mockupIds: next, excludedMockupIds: [] }) }}
            collection={coll ? {
              name: coll.name,
              mockupIds: (coll.mockups ?? []).map((m) => (m as { id: string }).id),
              onChange: async (next) => {
                const r = await fetch(`${getBackendUrl()}/api/mockups/collections/${coll.id}`, {
                  method: "PATCH",
                  headers: { ...authHeaders(), "Content-Type": "application/json" },
                  body: JSON.stringify({ mockupIds: next }),
                });
                if (!r.ok) { setMsg("Mockup grubu güncellenemedi."); throw new Error("grup"); }
                void qc.invalidateQueries({ queryKey: ["mockup-collections"] });
              },
            } : undefined}
            onPick={() => { /* current verildiginde kullanilmaz */ }}
            onClose={() => setAddFor(null)}
          />
        );
      })()}


      {mockupView && (() => {
        const row = review.find((x) => x.designId === mockupView.designId);
        if (!row) return null;
        return (
          <MockupFullscreen
            mockups={row.mockups}
            index={mockupView.index}
            lightUrl={abs(row.lightUrl)}
            darkUrl={abs(row.darkUrl)}
            pool={(row.designPool?.length ?? 0) > 1 ? row.designPool : undefined}
            onIndex={(i) => setMockupView((v) => (v ? { ...v, index: i } : v))}
            onClose={() => setMockupView(null)}
          />
        );
      })()}

      {assetFor && (() => {
        const row = review.find((x) => x.designId === assetFor);
        return (
          <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 p-4" onClick={() => setAssetFor(null)}>
            <div className="flex max-h-[88vh] w-full max-w-4xl flex-col overflow-hidden rounded-xl bg-white shadow-xl" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between border-b p-3">
                <p className="text-sm font-semibold text-gray-900">Görsel kütüphanesinden ekle — {row?.name}</p>
                <button onClick={() => setAssetFor(null)} className="rounded p-1 text-gray-400 hover:bg-gray-100"><X className="h-4 w-4" /></button>
              </div>
              <div className="overflow-y-auto p-3">
                <AssetLibrary
                  pickedUrls={row?.extraImages ?? []}
                  onPick={(a) => void assetApi.assign(a.id, { designIds: [assetFor] }).then(() => refetchReview())}
                />
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}

// ==== Liste paketleri ====
// ChatGPT (ya da panel) tasarim + SEO + kisisellestirme + sablonu paket olarak
// hazirlar. Mockup bekleyenler denetime eklenir; taslagi olusanlar burada
// durumuyla izlenir, Etsy'ye TASLAK olarak aktarilir ve dogrulanir.
interface PackageRow {
  designId: string; name: string; sku: string | null; status: string; statusTr: string; imageUrl: string;
  shop: string | null;
  draft: { id: string; title: string; status: string; etsyListingId: string | null; tagCount: number; etsyPersonalizationVerified: boolean | null } | null;
  checks: Array<{ level: "blocker" | "review" | "info"; code: string; message: string }>;
  seo: { title?: string; tags?: string[] } | null;
  personalization: unknown[];
  package: { sampleRenders: unknown[]; template: { version: number } | null; sellingMockupSuggestion?: string | null; inspirationSuggestion?: string | null };
}
const PKG_TONE: Record<string, string> = {
  awaiting_mockup: "bg-violet-50 text-violet-700", building: "bg-indigo-50 text-indigo-700", build_error: "bg-red-50 text-red-700",
  needs_review: "bg-amber-50 text-amber-700", ready_in_panel: "bg-emerald-50 text-emerald-700",
  etsy_draft: "bg-sky-50 text-sky-700", published: "bg-green-100 text-green-800",
};

async function fetchPackages(): Promise<PackageRow[]> {
  const r = await fetch(`${getBackendUrl()}/api/packages?limit=40`, { headers: authHeaders() });
  if (!r.ok) return [];
  return ((await r.json()).data ?? []) as PackageRow[];
}

function ChatGptPackages({ selected, onAdd }: { selected: Set<string>; onAdd: (ids: string[]) => void }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [note, setNote] = useState<Record<string, string>>({});
  const { data: pkgs = [] } = useQuery({
    queryKey: ["packages"],
    queryFn: fetchPackages,
    refetchInterval: (q) => ((q.state.data ?? []).some((p) => p.status === "building") ? 8000 : 30000),
  });
  const act = async (p: PackageRow, path: string, kind: string) => {
    setBusy(`${p.designId}:${kind}`);
    try {
      const r = await fetch(`${getBackendUrl()}/api/packages/${p.designId}/${path}`, { method: "POST", headers: authHeaders(), body: "{}" });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || "İşlem başarısız");
      const msg = path === "verify-etsy"
        ? (j.data.ok ? `Etsy ile eşleşiyor (${j.data.state ?? "?"})` : `Farklar: ${(j.data.mismatches as string[]).join(" · ")}`)
        : "Etsy'de taslak oluşturuluyor…";
      setNote((n) => ({ ...n, [p.designId]: msg }));
      void qc.invalidateQueries({ queryKey: ["packages"] });
    } catch (e) {
      setNote((n) => ({ ...n, [p.designId]: e instanceof Error ? e.message : String(e) }));
    } finally { setBusy(null); }
  };
  const awaiting = pkgs.filter((p) => p.status === "awaiting_mockup");
  const waiting = awaiting.filter((p) => !selected.has(p.designId));
  const inReview = awaiting.length - waiting.length;
  const inFlow = pkgs.filter((p) => p.status !== "awaiting_mockup");
  if (pkgs.length === 0) return null;
  const abs = (u: string) => (u.startsWith("http") ? u : `${getBackendUrl()}${u}`);
  return (
    <div className="mb-3 overflow-hidden rounded-xl border border-violet-200 bg-white">
      <div className="flex items-center gap-2 bg-violet-50 px-3 py-2">
        <button onClick={() => setOpen((o) => !o)} className="flex items-center gap-2 text-left">
          <Bot className="h-4 w-4 text-violet-600" />
          <span className="text-sm font-medium text-violet-900">Liste paketleri</span>
          <span className="text-xs text-violet-600">
            {pkgs.length} paket · {awaiting.length} mockup bekliyor{inReview > 0 ? ` (${inReview} denetimde)` : ""} · {inFlow.length} taslak/Etsy
          </span>
        </button>
        {waiting.length > 0 && (
          <button onClick={() => onAdd(waiting.map((d) => d.designId))}
            className="ml-auto rounded-lg bg-violet-600 px-3 py-1 text-xs font-medium text-white hover:bg-violet-700">
            Hepsini denetime ekle ({waiting.length})
          </button>
        )}
      </div>
      {open && waiting.length > 0 && (
        <div className="grid grid-cols-2 gap-2 p-3 sm:grid-cols-3 lg:grid-cols-5">
          {waiting.map((p) => (
            <div key={p.designId} className="flex flex-col rounded-lg border border-gray-200 p-2">
              <img src={abs(p.imageUrl)} alt=""
                className="mb-1.5 aspect-square w-full rounded bg-[repeating-conic-gradient(#f3f4f6_0_25%,#fff_0_50%)] bg-[length:12px_12px] object-contain" />
              <p className="truncate text-xs font-medium text-gray-800" title={p.seo?.title ?? p.name}>{p.seo?.title ?? p.name}</p>
              <p className="text-[10px] text-gray-500">
                {p.sku ?? "—"} · {p.seo?.tags?.length ?? 0} etiket
                {p.personalization.length ? ` · ${p.personalization.length} soru` : ""}
                {p.package.template ? " · şablon ✓" : ""}
                {p.package.sampleRenders.length > 1 ? ` · ${p.package.sampleRenders.length} örnek` : ""}
              </p>
              <ChatGptSuggestions compact selling={p.package.sellingMockupSuggestion} inspiration={p.package.inspirationSuggestion} />
              {(() => {
                const bad = p.checks.filter((c) => c.code.startsWith("member_") || c.code === "members_not_ready");
                return bad.length > 0 && (
                  <p className="mt-0.5 text-[10px] text-red-600" title={bad.map((c) => c.message).join(" · ")}>
                    Üye tasarımlarda {bad.filter((c) => c.code !== "members_not_ready").length} sorun — taslak kilitli
                  </p>
                );
              })()}
              <div className="mt-1.5 flex gap-1">
                <Link to={`/packages/${p.designId}`}
                  className="flex-1 rounded border border-gray-300 px-2 py-0.5 text-center text-[11px] font-medium text-gray-700 hover:bg-gray-50">
                  Düzenle
                </Link>
                <button onClick={() => onAdd([p.designId])}
                  className="flex-1 rounded border border-violet-300 px-2 py-0.5 text-[11px] font-medium text-violet-700 hover:bg-violet-50">
                  Denetime ekle
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
      {open && inFlow.length > 0 && (
        <div className="divide-y divide-gray-100 border-t border-violet-100">
          {inFlow.map((p) => {
            const blockers = p.checks.filter((c) => c.level === "blocker");
            const review = p.checks.filter((c) => c.level === "review");
            const canExport = p.draft && !p.draft.etsyListingId && p.draft.status !== "scheduled" && blockers.length === 0 && p.status !== "building";
            return (
              <div key={p.designId} className="flex items-start gap-3 px-3 py-2 text-xs">
                <img src={abs(p.imageUrl)} alt="" className="h-12 w-12 flex-shrink-0 rounded border border-gray-200 object-contain" />
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-1.5">
                    <span className={`rounded px-1.5 py-0.5 font-medium ${PKG_TONE[p.status] ?? "bg-gray-100 text-gray-600"}`}>{p.statusTr}</span>
                    <span className="truncate font-medium text-gray-800" title={p.draft?.title ?? p.name}>{p.draft?.title ?? p.name}</span>
                    <span className="text-gray-400">{p.sku}</span>
                    {p.draft && <Link to={`/listing-builder?id=${p.draft.id}`} className="text-indigo-600 hover:underline">Taslak →</Link>}
                    <Link to={`/packages/${p.designId}`} className="text-gray-500 hover:underline">Paket →</Link>
                  </div>
                  {[...blockers, ...review].slice(0, 4).map((c) => (
                    <p key={c.code + c.message} className={c.level === "blocker" ? "text-red-600" : "text-amber-700"}>• {c.message}</p>
                  ))}
                  {note[p.designId] && <p className="text-gray-600">{note[p.designId]}</p>}
                </div>
                <div className="flex flex-shrink-0 flex-col items-end gap-1">
                  {canExport && (
                    <button onClick={() => { if (confirm("Taslak Etsy'de TASLAK ilan olarak oluşturulsun mu? (Aktifleştirilmez)")) void act(p, "export-etsy", "export"); }}
                      disabled={busy !== null}
                      className="rounded-lg bg-sky-600 px-2.5 py-1 font-medium text-white hover:bg-sky-700 disabled:opacity-50">
                      {busy === `${p.designId}:export` ? "Aktarılıyor…" : "Etsy'ye taslak aktar"}
                    </button>
                  )}
                  {p.draft?.etsyListingId && (
                    <button onClick={() => void act(p, "verify-etsy", "verify")} disabled={busy !== null}
                      className="rounded-lg border border-sky-300 px-2.5 py-1 text-sky-700 hover:bg-sky-50 disabled:opacity-50">
                      {busy === `${p.designId}:verify` ? "Kontrol ediliyor…" : "Etsy'de doğrula"}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/** ChatGPT'nin yalniz yazi onerileri: satan mockup + ilham alinan liste (hicbir kayda baglanmaz) */
function ChatGptSuggestions({ selling, inspiration, compact = false }: { selling?: string | null; inspiration?: string | null; compact?: boolean }) {
  if (!selling && !inspiration) return null;
  const isUrl = (v: string) => /^https?:\/\//i.test(v.trim());
  return (
    <div className={`${compact ? "mt-1 text-[10px]" : "mt-1.5 text-xs"} space-y-0.5 rounded-md bg-violet-50/60 px-2 py-1 text-violet-900`}>
      {selling && <p><span className="font-medium">ChatGPT önerisi — satan mockup:</span> {selling}</p>}
      {inspiration && (
        <p className="truncate"><span className="font-medium">İlham önerisi:</span>{" "}
          {isUrl(inspiration)
            ? <a href={inspiration.trim()} target="_blank" rel="noreferrer" className="text-indigo-600 hover:underline">{inspiration.trim()} <ExternalLink className="inline h-2.5 w-2.5" /></a>
            : inspiration}
          <span className="text-violet-500"> (bağlı değil)</span>
        </p>
      )}
    </div>
  );
}

// ==== Ana urun fotografi (liste paketi) ====
// Kisisellestirilmis urunde Etsy ana gorselin tamamlanmis urunu gostermesini
// istiyor: gercek fotograf listenin ILK gorseli olur, mockuplar ek gorsel.
function MainPhotoControl({ designId, url, source, onChange }: { designId: string; url: string | null; source?: string | null; onChange: () => void }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const send = async (body: Record<string, unknown>) => {
    setBusy(true); setErr(null);
    try {
      const r = await fetch(`${getBackendUrl()}/api/designs/${designId}/main-photo`, { method: "POST", headers: authHeaders(), body: JSON.stringify(body) });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || "Kaydedilemedi");
      onChange();
    } catch (e) { setErr(e instanceof Error ? e.message : String(e)); } finally { setBusy(false); }
  };
  const pick = (f: File | undefined) => {
    if (!f) return;
    const rd = new FileReader();
    rd.onload = () => void send({ image: String(rd.result), source: "real_photo" });
    rd.readAsDataURL(f);
  };
  const abs = (u: string) => (u.startsWith("http") ? u : `${getBackendUrl()}${u}`);
  return (
    <span className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 ${url ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>
      {url && <img src={abs(url)} alt="" className="h-4 w-4 rounded object-cover" />}
      <label className="cursor-pointer hover:underline" title="Gerçek, kişiselleştirilmiş ürün fotoğrafı — listenin ana görseli olur">
        {busy ? "Yükleniyor…" : url ? (source === "generated_mockup" ? "Ana görsel: AI mockup (gerçek fotoğrafla değiştir)" : "Ana fotoğraf ✓ (değiştir)") : "Ana ürün fotoğrafı ekle"}
        <input type="file" accept="image/*" className="hidden" disabled={busy} onChange={(e) => pick(e.target.files?.[0])} />
      </label>
      {url && !busy && <button onClick={() => void send({ remove: true })} className="text-gray-400 hover:text-red-600" title="Kaldır">×</button>}
      {err && <span className="text-red-600">{err}</span>}
    </span>
  );
}

// ==== Liste paketi: duzenlenebilir sablon + ornekler ====
// Custom urun: sabit grafik + degisken metin katmanlari. Ornek isimler AYNI
// sablondan basilir (her isim icin AI gorseli uretilmez); siparis de buradan.
function PackageTemplatePanel({ designId, info, onChange }: {
  designId: string;
  info: NonNullable<DesignReview["packageInfo"]>;
  onChange: () => void;
}) {
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const call = async (method: string, path: string, body: unknown, kind: string) => {
    setBusy(kind); setErr(null);
    try {
      const r = await fetch(`${getBackendUrl()}/api/packages/${designId}${path}`, { method, headers: authHeaders(), body: JSON.stringify(body) });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || j.message || "İşlem başarısız");
      onChange();
    } catch (e) { setErr(e instanceof Error ? e.message : String(e)); } finally { setBusy(null); }
  };
  const abs = (u: string) => (u.startsWith("http") ? u : `${getBackendUrl()}${u}`);
  const issues = info.issues.filter((i) => i.level !== "info");
  return (
    <div className="mt-2 rounded-lg border border-violet-100 bg-violet-50/40 p-2 text-[11px]">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-medium text-violet-900">Düzenlenebilir şablon:</span>
        {info.hasTemplate ? (
          <span className="rounded bg-emerald-50 px-1.5 py-0.5 text-emerald-700">✓ sürüm {info.templateVersion}{info.printSide ? ` · ${info.printSide === "both" ? "ön+arka" : info.printSide === "back" ? "arka" : "ön"}` : ""}</span>
        ) : (
          <span className={`rounded px-1.5 py-0.5 ${info.custom ? "bg-amber-50 text-amber-700" : "bg-gray-100 text-gray-600"}`}>
            yok{info.custom ? " — siparişte yeni isimle basılamaz" : ""}
          </span>
        )}
        <button onClick={() => void call("POST", "/template", { mode: "existing_art" }, "existing")} disabled={busy !== null}
          title="Görsel zaten yazısız sabit grafik: kayıtlı katman yerleşimiyle şablon kurar (tespit/silme/AI yok, ücretsiz)"
          className="rounded border border-emerald-300 bg-white px-2 py-0.5 text-emerald-700 hover:bg-emerald-50 disabled:opacity-50">
          {busy === "existing" ? "Kuruluyor…" : "Mevcut grafik + kayıtlı katmanlar"}
        </button>
        <button onClick={() => void call("POST", "/template", { mode: "detect", artMode: "mask" }, "mask")} disabled={busy !== null}
          title="Tasarımdaki örnek yazıları bulup katmana çevirir, sanattan kodla siler (AI ile yazı tespiti, düşük maliyet)"
          className="rounded border border-violet-300 bg-white px-2 py-0.5 text-violet-700 hover:bg-violet-50 disabled:opacity-50">
          {busy === "mask" ? "Çevriliyor…" : info.hasTemplate ? "Yeniden çıkar (kod)" : "Şablona çevir (kod)"}
        </button>
        <button onClick={() => void call("POST", "/template", { mode: "detect", artMode: "ai" }, "ai")} disabled={busy !== null}
          title="Yazıları AI ile siler — daha temiz sanat, ücretli"
          className="rounded border border-fuchsia-300 bg-white px-2 py-0.5 text-fuchsia-700 hover:bg-fuchsia-50 disabled:opacity-50">
          {busy === "ai" ? "Çevriliyor…" : "AI ile temizle"}
        </button>
        {info.hasTemplate && (
          <label className="flex items-center gap-1 text-gray-600" title="Koyu tişörtlerde isim/numara bu renkle basılır (mockupta koyu alanlara koyu varyant gelir)">
            Koyu üründe yazı
            <select defaultValue="" disabled={busy !== null}
              onChange={(e) => { if (e.target.value) void call("PATCH", "/template", { darkTextColor: e.target.value }, "dark"); }}
              className="rounded border border-gray-200 px-1 py-0 text-[11px]">
              <option value="">seç…</option>
              <option value="auto">otomatik (koyu yazıysa krem)</option>
              <option value="#f4efe6">krem</option>
              <option value="#ffffff">beyaz</option>
              <option value="none">varyant yok</option>
            </select>
          </label>
        )}
        {info.sampleRenders.length > 1 && (
          <label className="ml-auto flex items-center gap-1 text-gray-600">
            Mockuplarda
            <select value={info.sampleMode} disabled={busy !== null}
              onChange={(e) => void call("PATCH", "/template", { sampleMode: e.target.value }, "mode")}
              className="rounded border border-gray-200 px-1 py-0 text-[11px]">
              <option value="distributed">dağıtılmış örnekler</option>
              <option value="same">hep aynı örnek</option>
            </select>
          </label>
        )}
      </div>
      {info.sampleRenders.length > 0 && (
        <div className="mt-1.5 flex flex-wrap gap-2">
          {info.sampleRenders.map((s, i) => (
            <div key={i} className="w-20 text-center">
              <img src={abs(s.url)} alt="" className="h-20 w-20 rounded border border-gray-200 bg-[repeating-conic-gradient(#f3f4f6_0_25%,#fff_0_50%)] bg-[length:10px_10px] object-contain" />
              {s.darkUrl && <img src={abs(s.darkUrl)} alt="" title="Koyu ürün varyantı" className="mt-0.5 h-20 w-20 rounded border border-gray-700 bg-[#3d4249] object-contain" />}
              {s.backUrl && <img src={abs(s.backUrl)} alt="" title="Arka yüz" className="mt-0.5 h-10 w-20 rounded border border-gray-200 object-contain" />}
              <p className="truncate text-[10px] text-gray-600" title={Object.values(s.values).join(" ")}>{Object.values(s.values).join(" ")}</p>
            </div>
          ))}
        </div>
      )}
      {issues.length > 0 && (
        <ul className="mt-1 space-y-0.5">
          {issues.map((i, k) => <li key={k} className={i.level === "blocker" ? "text-red-600" : "text-amber-700"}>• {i.message}</li>)}
        </ul>
      )}
      {err && <p className="mt-1 text-red-600">{err}</p>}
    </div>
  );
}

/** Liste Denetimi: baslik / aciklama / etiket goruntule + elle duzenle.
 *  Kayit tasarimin SEO'suna yazilir; taslak once onu kullanir. */
function SeoEditor({ designId, listing, onSaved }: {
  designId: string;
  listing: { title: string; description: string; tags: string[] };
  onSaved: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState(listing.title);
  const [description, setDescription] = useState(listing.description);
  const [tags, setTags] = useState(listing.tags.join(", "));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tips, setTips] = useState<string[]>([]);
  const tagList = tags.split(",").map((t) => t.trim()).filter(Boolean);

  const open = () => {
    setTitle(listing.title); setDescription(listing.description); setTags(listing.tags.join(", "));
    setError(null); setTips([]); setEditing(true);
  };
  const save = async () => {
    setSaving(true); setError(null);
    try {
      const r = await fetch(`${getBackendUrl()}/api/listing-review/seo`, {
        method: "PUT", headers: authHeaders(), body: JSON.stringify({ designId, title, description, tags: tagList }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) { setError(j.error || "Kaydedilemedi"); setTips(j.tips ?? []); return; }
      setTips(j.data?.tips ?? []);
      setEditing(false);
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  };

  if (!editing) {
    return (
      <div>
        <div className="flex items-start gap-2">
          <p className="flex-1 text-gray-800"><span className="text-gray-400">Başlık:</span> {listing.title}</p>
          <button onClick={open} className="shrink-0 rounded border border-gray-300 px-1.5 py-0.5 text-[10px] text-gray-700 hover:bg-gray-50">
            Düzenle
          </button>
        </div>
        <p className="mt-0.5 line-clamp-3 text-gray-600">
          <span className="text-gray-400">Açıklama:</span> {listing.description.slice(0, 300) || "—"}
        </p>
        <p className="mt-0.5 text-gray-600">
          <span className="text-gray-400">Etiket ({listing.tags.length}):</span>{" "}
          {listing.tags.length > 0 ? listing.tags.join(", ") : "—"}
        </p>
        {tips.length > 0 && <p className="mt-0.5 text-amber-700">Öneri: {tips.join(" · ")}</p>}
      </div>
    );
  }
  return (
    <div className="space-y-1.5">
      <label className="block">
        <span className="flex justify-between text-gray-500">Başlık <span className={title.length > 140 ? "text-red-600" : ""}>{title.length}/140</span></span>
        <input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={140}
          className="w-full rounded border border-gray-300 px-2 py-1 text-[11px] focus:border-indigo-400 focus:outline-none" />
      </label>
      <label className="block">
        <span className="text-gray-500">Açıklama</span>
        <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={8}
          className="w-full rounded border border-gray-300 px-2 py-1 text-[11px] focus:border-indigo-400 focus:outline-none" />
      </label>
      <label className="block">
        <span className="flex justify-between text-gray-500">
          Etiketler (virgülle ayır) <span className={tagList.length > 13 ? "text-red-600" : ""}>{tagList.length}/13</span>
        </span>
        <input value={tags} onChange={(e) => setTags(e.target.value)}
          className="w-full rounded border border-gray-300 px-2 py-1 text-[11px] focus:border-indigo-400 focus:outline-none" />
        {tagList.some((t) => t.length > 20) && (
          <span className="text-red-600">20 karakteri geçen: {tagList.filter((t) => t.length > 20).join(", ")}</span>
        )}
      </label>
      {error && <p className="text-red-600">{error}</p>}
      {tips.length > 0 && <p className="text-amber-700">Öneri: {tips.join(" · ")}</p>}
      <div className="flex gap-1.5">
        <button onClick={() => void save()} disabled={saving || !title.trim()}
          className="rounded bg-indigo-600 px-2.5 py-1 text-[11px] font-medium text-white hover:bg-indigo-700 disabled:opacity-50">
          {saving ? "Kaydediliyor…" : "Kaydet"}
        </button>
        <button onClick={() => setEditing(false)} className="rounded border border-gray-300 px-2.5 py-1 text-[11px] text-gray-700 hover:bg-gray-50">
          İptal
        </button>
      </div>
    </div>
  );
}
